import { OrthogonalSegment } from '../objects/connector';
import { Rect as RectModel } from '../primitives/rect';
import { Point } from '../primitives/point';
import { findDistance, getConnectorDirection } from '../utility/diagram-util';
/**
 * Connects diagram objects with layout algorithm
 */
export class ComplexHierarchicalTree {
    /**
     * Constructor for the hierarchical tree layout module
     *
     * @private
     */
    constructor() {
        //constructs the layout module
    }
    /**
     * To destroy the hierarchical tree module
     *
     * @returns {void}
     * @private
     */
    destroy() {
        /**
         * Destroy method performed here
         */
    }
    /**
     * Core method to return the component name.
     *
     * @returns {string}  Core method to return the component name.
     * @private
     */
    getModuleName() {
        /**
         * Returns the module name of the layout
         *
         */
        return 'ComplexHierarchicalTree';
    }
    /**
     * doLayout method\
     *
     * @returns {  void }    doLayout method .\
     * @param {INode[]} nodes - provide the nodes value.
     * @param {{}} nameTable - provide the nameTable value.
     * @param {Layout} layout - provide the layout value.
     * @param {PointModel} viewPort - provide the viewPort value.
     * @param {Diagram} diagram - provide the diagram model.
     * @private
     */
    doLayout(nodes, nameTable, layout, viewPort, diagram) {
        new HierarchicalLayoutUtil().doLayout(nodes, nameTable, layout, viewPort, diagram);
    }
    getLayoutNodesCollection(nodes) {
        const nodesCollection = [];
        let node;
        const parentId = 'parentId';
        const processId = 'processId';
        for (let i = 0; i < nodes.length; i++) {
            node = nodes[parseInt(i.toString(), 10)];
            //885697:Position of root node without the child node in complex hierarchical layout is not proper
            // 941582: ExcludeFromLayout Option Not Supported in Complex Hierarchical Tree
            if (!node.excludeFromLayout) {
                if (((node.inEdges.length + node.outEdges.length > 0) || (node.offsetX === 0 && node.offsetY === 0)) &&
                    !node['' + parentId] && !node['' + processId]) {
                    nodesCollection.push(node);
                }
            }
        }
        return nodesCollection;
    }
}
/**
 * Utility that arranges the nodes in hierarchical structure
 */
class HierarchicalLayoutUtil {
    constructor() {
        this.nameTable = {};
        this.vertexTable = {};
        this.verticesMap = new Map();
        this.crossReduction = new CrossReduction();
        /**
         * The preferred vertical offset between edges exiting a vertex Default is 2.
         */
        this.previousEdgeOffset = 6;
        /**
         * The preferred horizontal distance between edges exiting a vertex Default is 5.
         */
        this.previousEdgeDistance = 5;
        /**
         * Holds the collection vertices, that are equivalent to nodes to be arranged
         */
        this.jettyPositions = {};
        /**
         * Internal cache of bottom-most value of Y for each rank
         */
        this.rankBottomY = null;
        /**
         * Internal cache of bottom-most value of X for each rank
         */
        this.limitX = null;
        /**
         * Internal cache of top-most values of Y for each rank
         */
        this.rankTopY = null;
        /**
         * The minimum parallelEdgeSpacing value is 12.
         */
        this.parallelEdgeSpacing = 10;
        /**
         * The minimum distance for an edge jetty from a vertex Default is 12.
         */
        this.minEdgeJetty = 12;
    }
    //Defines a vertex that is equivalent to a node object
    createVertex(node, value, x, y, width, height) {
        const geometry = { x: x, y: y, width: width, height: height };
        const vertex = {
            value: value, geometry: geometry, name: value, vertex: true,
            inEdges: node.inEdges.slice(), outEdges: node.outEdges.slice(),
            maxRowWidth: undefined, maxRowHeight: undefined
        };
        return vertex;
    }
    /**
     * Initializes the edges collection of the vertices\
     *
     * @returns {  IConnector[] }    Initializes the edges collection of the vertices\
     * @param {Vertex} node - provide the node value.
     * @private
     */
    getEdges(node) {
        const edges = [];
        const node1 = this.nameTable[node.name];
        // 941582: ExcludeFromLayout Option Not Supported in Complex Hierarchical Tree
        if (node && !node1.excludeFromLayout) {
            for (let i = 0; node.inEdges.length > 0 && i < node.inEdges.length; i++) {
                const connector = this.nameTable[node.inEdges[parseInt(i.toString(), 10)]];
                if (!this.nameTable[connector.sourceID].excludeFromLayout) {
                    edges.push(connector);
                }
            }
            for (let i = 0; node.outEdges.length > 0 && i < node.outEdges.length; i++) {
                const connector = this.nameTable[node.outEdges[parseInt(i.toString(), 10)]];
                if (!this.nameTable[connector.targetID].excludeFromLayout) {
                    edges.push(connector);
                }
            }
        }
        return edges;
    }
    //Finds the root nodes of the layout
    findRoots(vertices, isMatrixArrangment) {
        const roots = [];
        let best = null;
        let maxDiff = -100000;
        for (const i of Object.keys(vertices)) {
            const cell = vertices[`${i}`];
            const conns = this.getEdges(cell);
            let outEdges = 0;
            let inEdges = 0;
            for (let k = 0; k < conns.length; k++) {
                const src = this.getVisibleTerminal(conns[parseInt(k.toString(), 10)], true);
                if (src.name === cell.name) {
                    outEdges++;
                }
                else {
                    inEdges++;
                }
            }
            //951668 - Independent nodes get overlapped in complex hierarchical tree layout
            if (inEdges === 0 && (isMatrixArrangment || outEdges > 0)) {
                roots.push(cell);
            }
            const diff = outEdges - inEdges;
            if (diff > maxDiff) {
                maxDiff = diff;
                best = cell;
            }
        }
        if (roots.length === 0 && best != null) {
            roots.push(best);
        }
        return roots;
    }
    /**
     * Returns the source/target vertex of the given connector \
     *
     * @returns {  Vertex }    Returns the source/target vertex of the given connector \
     * @param {IConnector} edge - provide the node value.
     * @param {boolean} source - provide the node value.
     * @private
     */
    getVisibleTerminal(edge, source) {
        let terminalCache = this.nameTable[edge.targetID];
        if (source) {
            terminalCache = this.nameTable[edge.sourceID];
        }
        //Removed loop and used Map
        return this.verticesMap.get(terminalCache.id) || null;
    }
    /**
     * Traverses each sub tree, ensures there is no cycle in traversing \
     *
     * @returns {  {} }    Traverses each sub tree, ensures there is no cycle in traversing .\
     * @param {Vertex} vertex - provide the vertex value.
     * @param {boolean} directed - provide the directed value.
     * @param {IConnector} edge - provide the edge value.
     * @param {{}} currentComp - provide the currentComp value.
     * @param {{}[]} hierarchyVertices - provide the hierarchyVertices value.
     * @param {{}} filledVertices - provide the filledVertices value.
     * @private
     */
    traverse(vertex, directed, edge, currentComp, hierarchyVertices, filledVertices) {
        if (vertex != null) {
            const vertexID = vertex.name;
            if ((filledVertices == null ? true : filledVertices[`${vertexID}`] != null)) {
                if (currentComp[`${vertexID}`] == null) {
                    currentComp[`${vertexID}`] = vertex;
                }
                if (filledVertices != null) {
                    delete filledVertices[`${vertexID}`];
                }
                const edges = this.getEdges(vertex);
                const edgeIsSource = [];
                for (let i = 0; i < edges.length; i++) {
                    edgeIsSource[parseInt(i.toString(), 10)] = this.getVisibleTerminal(edges[parseInt(i.toString(), 10)], true) === vertex;
                }
                for (let i = 0; i < edges.length; i++) {
                    if (!directed || edgeIsSource[parseInt(i.toString(), 10)]) {
                        const next = this.getVisibleTerminal(edges[parseInt(i.toString(), 10)], !edgeIsSource[parseInt(i.toString(), 10)]);
                        let netCount = 1;
                        for (let j = 0; j < edges.length; j++) {
                            if (j === i) {
                                continue;
                            }
                            else {
                                const isSource2 = edgeIsSource[parseInt(j.toString(), 10)];
                                const otherTerm = this.getVisibleTerminal(edges[parseInt(j.toString(), 10)], !isSource2);
                                if (otherTerm === next) {
                                    if (isSource2) {
                                        netCount++;
                                    }
                                    else {
                                        netCount--;
                                    }
                                }
                            }
                        }
                        if (netCount >= 0) {
                            currentComp = this.traverse(next, directed, edges[parseInt(i.toString(), 10)], currentComp, hierarchyVertices, filledVertices);
                        }
                    }
                }
            }
            else {
                if (currentComp[`${vertexID}`] == null) {
                    // We've seen this vertex before, but not in the current component This component and the one it's in need to be merged
                    for (let i = 0; i < hierarchyVertices.length; i++) {
                        const comp = hierarchyVertices[parseInt(i.toString(), 10)];
                        if (comp[`${vertexID}`] != null) {
                            for (const key of Object.keys(comp)) {
                                currentComp[`${key}`] = comp[`${key}`];
                            }
                            // Remove the current component from the hierarchy set
                            hierarchyVertices.splice(i, 1);
                            return currentComp;
                        }
                    }
                }
            }
        }
        return currentComp;
    }
    //Returns the bounds of the given vertices
    getModelBounds(nodes) {
        nodes = nodes.slice();
        let rect = null;
        let rect1 = null;
        for (let i = 0; i < nodes.length; i++) {
            rect = nodes[parseInt(i.toString(), 10)].geometry;
            if (rect1) {
                const right = Math.max(rect1.x + rect1.width, rect.x + rect.width);
                const bottom = Math.max(rect1.y + rect1.height, rect.y + rect.height);
                rect1.x = Math.min(rect1.x, rect.x);
                rect1.y = Math.min(rect1.y, rect.y);
                rect1.width = right - rect1.x;
                rect1.height = bottom - rect1.y;
            }
            else {
                rect1 = { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
            }
        }
        return rect1;
    }
    /* tslint:disable */
    /**
     *  Initializes the layouting process \
     *
     * @returns {  Vertex }     Initializes the layouting process \
     * @param {INode[]} nodes - provide the node value.
     * @param {{}} nameTable - provide the nameTable value.
     * @param {Layout} layoutProp - provide the layoutProp value.
     * @param {PointModel} viewPort - provide the viewPort value.
     * @param {Diagram} diagram - provide the diagram model.
     * @private
     */
    doLayout(nodes, nameTable, layoutProp, viewPort, diagram) {
        this.nameTable = nameTable;
        const canEnableRouting = layoutProp.enableRouting;
        const layout = {
            horizontalSpacing: layoutProp.horizontalSpacing, verticalSpacing: layoutProp.verticalSpacing,
            orientation: layoutProp.orientation, marginX: layoutProp.margin.left, marginY: layoutProp.margin.top,
            enableLayoutRouting: canEnableRouting
        };
        let model;
        const matrixModel = new MatrixModel();
        matrixModel.edgeMapper = [];
        matrixModel.diagram = diagram;
        const nodeWithMultiEdges = [];
        this.vertices = [];
        const filledVertexSet = {};
        for (let i = 0; i < nodes.length; i++) {
            const node1 = this.nameTable[nodes[parseInt(i.toString(), 10)].id];
            // 941582: ExcludeFromLayout Option Not Supported in Complex Hierarchical Tree
            if (!node1.excludeFromLayout) {
                const node = this.createVertex(nodes[parseInt(i.toString(), 10)], nodes[parseInt(i.toString(), 10)].id, 0, 0, nodes[parseInt(i.toString(), 10)].actualSize.width, nodes[parseInt(i.toString(), 10)].actualSize.height);
                this.vertices.push(node);
                this.verticesMap.set(node.name, node);
                this.vertexTable[node1.id] = node;
                if (nodes[parseInt(i.toString(), 10)].inEdges.length > 0
                    || nodes[parseInt(i.toString(), 10)].outEdges.length > 0) {
                    nodeWithMultiEdges.push(nodes[parseInt(i.toString(), 10)]);
                }
                filledVertexSet[node.name] = node;
                if (matrixModel) {
                    const outEdges = nodes[parseInt(i.toString(), 10)].outEdges.slice();
                    for (let j = 0; j < outEdges.length; j++) {
                        const outEdge = nameTable[outEdges[parseInt(j.toString(), 10)]];
                        matrixModel.setEdgeMapper({ key: outEdge, value: [] });
                    }
                }
            }
        }
        const hierarchyVertices = [];
        //let candidateRoots: Vertex[];
        let isMatrixArrangement = layoutProp.arrangement === 'Linear' || canEnableRouting;
        isMatrixArrangement = isMatrixArrangement || (matrixModel && layoutProp.connectionPointOrigin === 'DifferentPoint');
        const candidateRoots = this.findRoots(filledVertexSet, isMatrixArrangement);
        for (let i = 0; i < candidateRoots.length; i++) {
            const vertexSet = {};
            hierarchyVertices.push(vertexSet);
            this.traverse(candidateRoots[parseInt(i.toString(), 10)], true, null, vertexSet, hierarchyVertices, filledVertexSet);
        }
        let limit = { marginX: 0, marginY: 0 };
        let tmp = [];
        let checkLinear = false;
        let matrixModelObject;
        for (let i = 0; i < hierarchyVertices.length; i++) {
            const vertexSet = hierarchyVertices[parseInt(i.toString(), 10)];
            // eslint-disable-next-line
            for (const key of Object.keys(vertexSet)) {
                tmp.push(vertexSet[`${key}`]);
            }
            if ((layoutProp.arrangement === 'Linear' && i === hierarchyVertices.length - 1) || canEnableRouting) {
                checkLinear = true;
            }
            model = new MultiParentModel(this, tmp, candidateRoots, layout);
            this.cycleStage(model);
            this.layeringStage(model);
            const ranks = model.ranks;
            for (let x = ranks.length - 1; x >= 0; x--) {
                let maxWidth = 0;
                let maxHeight = 0;
                for (const rank of ranks[parseInt(x.toString(), 10)]) {
                    if (this.getType(rank.type) === 'internalVertex') {
                        const geometry = rank.cell.geometry;
                        maxWidth = Math.max(maxWidth, geometry.width);
                        maxHeight = Math.max(maxHeight, geometry.height);
                    }
                }
                for (const rank of ranks[parseInt(x.toString(), 10)]) {
                    if (this.getType(rank.type) === 'internalVertex') {
                        const cell = rank.cell;
                        cell.maxRowWidth = maxWidth;
                        cell.maxRowHeight = maxHeight;
                    }
                }
            }
            //897503: Child Nodes position in ComplexHierarchicalTree updated wrongly results in connector overlap
            if ((matrixModel && layoutProp.connectionPointOrigin === 'DifferentPoint') || checkLinear) {
                matrixModelObject = { model: model, matrix: [], rowOffset: [], roots: [] };
                matrixModel.arrangeElements(matrixModelObject, layoutProp);
                layoutProp.ranks = matrixModelObject.model.ranks;
            }
            else {
                if (layoutProp.arrangement === 'Nonlinear') {
                    this.crossingStage(model);
                    limit = this.placementStage(model, limit.marginX, limit.marginY);
                    tmp = [];
                }
            }
        }
        const modelBounds = this.getModelBounds(this.vertices);
        this.updateMargin(layoutProp, layout, modelBounds, viewPort);
        for (let i = 0; i < this.vertices.length; i++) {
            const clnode = this.vertices[parseInt(i.toString(), 10)];
            if (clnode) { //Check what is node.source/node.target -  && !clnode.source && !clnode.target) {
                const dnode = this.nameTable[clnode.name];
                dnode.offsetX = 0;
                dnode.offsetY = 0;
                //initialize layout
                const dx = (clnode.geometry.x - (dnode.offsetX - (dnode.actualSize.width / 2))) + layout.marginX;
                const dy = (clnode.geometry.y - (dnode.offsetY - (dnode.actualSize.height / 2))) + layout.marginY;
                let x = dx;
                let y = dy;
                if (layout.orientation === 'BottomToTop') {
                    if (canEnableRouting) {
                        clnode.geometry.y = modelBounds.height - dy - dnode.actualSize.height / 2;
                    }
                    y = modelBounds.height - dy;
                }
                else if (layout.orientation === 'RightToLeft') {
                    x = modelBounds.width - dx;
                }
                dnode.offsetX += x - dnode.offsetX;
                dnode.offsetY += y - dnode.offsetY;
                if (layoutProp.horizontalAlignment !== 'Stretch' || layoutProp.verticalAlignment !== 'Stretch') {
                    matrixModel.nodePropertyChange(dnode);
                }
            }
        }
        if (!checkLinear) {
            for (let i = 0; i < this.vertices.length; i++) {
                this.isNodeOverLap(this.nameTable[this.vertices[parseInt(i.toString(), 10)].name], layoutProp);
            }
        }
        matrixModel.updateLayout(viewPort, modelBounds, layoutProp, layout, nodeWithMultiEdges, nameTable, this.vertexTable);
        if (canEnableRouting) {
            const vertices = {};
            let matrixrow1;
            for (let p = 0; p < matrixModelObject.matrix.length; p++) {
                matrixrow1 = matrixModelObject.matrix[parseInt(p.toString(), 10)].value;
                for (let q = 0; q < matrixrow1.length; q++) {
                    const matrixCell = matrixrow1[parseInt(q.toString(), 10)];
                    for (let r = 0; r < matrixCell.cells.length; r++) {
                        const cell = matrixCell.cells[parseInt(r.toString(), 10)];
                        const type = this.getType(cell.type);
                        if (type === 'internalVertex') {
                            const internalVertex = cell;
                            vertices[internalVertex.id] = internalVertex;
                        }
                    }
                }
            }
            this.updateRankValuess(model);
            for (let i = 0, a = Object.keys(vertices); i < a.length; i++) {
                const key = a[parseInt(i.toString(), 10)];
                this.setVertexLocationValue(vertices[`${key}`], layoutProp.orientation, modelBounds);
            }
            this.localEdgeProcessing(model, vertices);
            this.assignRankOffset(model);
            this.updateEdgeSetXYValue(model);
            const edges = this.getValues(model.edgeMapper);
            for (let i = 0; i < edges.length; i++) {
                if ((edges[parseInt(i.toString(), 10)]).x.length > 0) {
                    for (let j = 0; j < (edges[parseInt(i.toString(), 10)]).x.length; j++) {
                        if (layoutProp.orientation !== 'RightToLeft' && layoutProp.orientation !== 'LeftToRight') {
                            (edges[parseInt(i.toString(), 10)]).x[parseInt(j.toString(), 10)]
                                = (edges[parseInt(i.toString(), 10)]).x[parseInt(j.toString(), 10)] + layout.marginX;
                        }
                        else if (layoutProp.orientation === 'LeftToRight') {
                            (edges[parseInt(i.toString(), 10)]).x[parseInt(j.toString(), 10)]
                                = (edges[parseInt(i.toString(), 10)]).x[parseInt(j.toString(), 10)] + layoutProp.verticalSpacing / 2;
                        }
                        else {
                            (edges[parseInt(i.toString(), 10)]).x[parseInt(j.toString(), 10)]
                                = (edges[parseInt(i.toString(), 10)]).x[parseInt(j.toString(), 10)] + layoutProp.verticalSpacing / 2;
                        }
                    }
                }
                this.setEdgePosition(edges[parseInt(i.toString(), 10)], model, layout);
            }
            for (let p = 0; p < this.vertices.length; p++) {
                const clnode = this.vertices[parseInt(p.toString(), 10)];
                if (clnode.outEdges.length > 1) {
                    this.updateMultiOutEdgesPoints(clnode);
                }
            }
        }
    }
    setEdgeXY(ranks, node, spacing, layer) {
        if (ranks && node.source.id) {
            let targetValue;
            let sourceValue;
            for (let i = 0; i < ranks.length; i++) {
                for (let k = 0; k < ranks[parseInt(i.toString(), 10)].length; k++) {
                    if (ranks[parseInt(i.toString(), 10)][parseInt(k.toString(), 10)].id === node.target.id
                        || ranks[parseInt(i.toString(), 10)][parseInt(k.toString(), 10)].id === node.source.id) {
                        if (ranks[parseInt(i.toString(), 10)][parseInt(k.toString(), 10)].id === node.target.id
                            && targetValue === undefined) {
                            targetValue = i;
                        }
                        if (ranks[parseInt(i.toString(), 10)][parseInt(k.toString(), 10)].id === node.source.id
                            && sourceValue === undefined) {
                            sourceValue = i;
                        }
                    }
                }
            }
            let rankOffsetValue;
            for (let m = targetValue; m <= sourceValue; m++) {
                if (rankOffsetValue === undefined) {
                    rankOffsetValue = this[m + '_RankOffset'];
                }
                if (rankOffsetValue !== undefined && rankOffsetValue < this[m + '_RankOffset']) {
                    rankOffsetValue = this[m + '_RankOffset'];
                }
            }
            if (this['edges'] === undefined) {
                this['edges'] = {};
            }
            this['edges'][(node).ids[0]] = { x: node.x, y: 0 };
            const value = this.resetOffsetXValue(rankOffsetValue, spacing / 10);
            node.x[layer - node.minRank - 1] = value;
            for (let k = 0; k < (node).edges.length; k++) {
                (node).edges[parseInt(k.toString(), 10)]['levelSkip'] = true;
            }
        }
    }
    resetOffsetXValue(value, spacing) {
        for (let i = 0, a = Object.keys(this['edges']); i < a.length; i++) {
            const key = a[parseInt(i.toString(), 10)];
            const length = this['edges'][`${key}`].x;
            for (let j = 0; j < length.length; j++) {
                let offsetValue;
                if (this['edges'][`${key}`].x[parseInt(j.toString(), 10)] === value) {
                    offsetValue = value + spacing;
                    offsetValue = this.resetOffsetXValue(offsetValue, spacing);
                    return offsetValue;
                }
            }
        }
        return value;
    }
    setEdgePosition(cell, model, layout) {
        // For parallel edges we need to seperate out the points a
        // little
        let offsetX = 0;
        // Only set the edge control points once
        if (cell.temp[0] !== 101207) {
            if (cell.maxRank === undefined) {
                cell.maxRank = -1;
            }
            if (cell.minRank === undefined) {
                cell.minRank = -1;
            }
            let maxRank = cell.maxRank;
            let minRank = cell.minRank;
            if (maxRank === minRank) {
                maxRank = cell.source.maxRank;
                minRank = cell.target.minRank;
            }
            let parallelEdgeCount = 0;
            const jettys = this.jettyPositions[cell.ids[0]];
            if (cell.isReversed === undefined) {
                cell.isReversed = false;
            }
            else {
                cell.isReversed = true;
            }
            const source = cell.isReversed ? cell.target.cell : cell.source.cell;
            let layoutReversed = false;
            if (model.layout.orientation === 'TopToBottom' || model.layout.orientation === 'LeftToRight') {
                if (model.layout.orientation === 'TopToBottom') {
                    layoutReversed = false;
                }
                if (model.layout.orientation === 'LeftToRight') {
                    if (!cell.isReversed) {
                        layoutReversed = false;
                    }
                    else {
                        layoutReversed = false;
                    }
                }
            }
            else {
                if (!cell.isReversed) {
                    layoutReversed = true;
                }
            }
            for (let i = 0; i < cell.edges.length; i++) {
                const realEdge = cell.edges[parseInt(i.toString(), 10)];
                const realSource = this.getVisibleTerminal(realEdge, true);
                //List oldPoints = graph.getPoints(realEdge);
                const newPoints = [];
                // Single length reversed edges end up with the jettys in the wrong
                // places. Since single length edges only have jettys, not segment
                // control points, we just say the edge isn't reversed in this section
                let reversed = cell.isReversed;
                // if(cell.isReversed===undefined){
                //     reversed = false
                // }else{
                //     reversed =cell.isReversed
                // }
                if (realSource !== source) {
                    // The real edges include all core model edges and these can go
                    // in both directions. If the source of the hierarchical model edge
                    // isn't the source of the specific real edge in this iteration
                    // treat if as reversed
                    reversed = !reversed;
                }
                // First jetty of edge
                if (jettys != null) {
                    const arrayOffset = reversed ? 2 : 0;
                    let y = reversed ?
                        (layoutReversed ? this.rankBottomY[parseInt(minRank.toString(), 10)]
                            : this.rankTopY[parseInt(minRank.toString(), 10)])
                        : (layoutReversed ? this.rankTopY[parseInt(maxRank.toString(), 10)]
                            : this.rankBottomY[parseInt(maxRank.toString(), 10)]);
                    let jetty = jettys[parallelEdgeCount * 4 + 1 + arrayOffset];
                    if (reversed !== layoutReversed) {
                        jetty = -jetty;
                    }
                    if (layout.orientation === 'TopToBottom' || layout.orientation === 'BottomToTop') {
                        y += jetty;
                    }
                    const x = jettys[parallelEdgeCount * 4 + arrayOffset];
                    if (layout.orientation === 'TopToBottom' || layout.orientation === 'BottomToTop') {
                        newPoints.push(this.getPointvalue(x, y + layout.marginY));
                    }
                    else {
                        if (layout.orientation === 'LeftToRight') {
                            newPoints.push(this.getPointvalue(y + jetty, x + layout.marginY));
                        }
                        else {
                            newPoints.push(this.getPointvalue(y, x + layout.marginY));
                        }
                    }
                }
                let loopStart = cell.x.length - 1;
                let loopLimit = -1;
                let loopDelta = -1;
                let currentRank = cell.maxRank - 1;
                if (reversed) {
                    loopStart = 0;
                    loopLimit = cell.x.length;
                    loopDelta = 1;
                    currentRank = cell.minRank + 1;
                }
                // Reversed edges need the points inserted in
                // reverse order
                for (let j = loopStart; (cell.maxRank !== cell.minRank) && j !== loopLimit; j += loopDelta) {
                    // The horizontal position in a vertical layout
                    const positionX = cell.x[parseInt(j.toString(), 10)] + offsetX;
                    // This cell.x determines the deviated points of the connectors and jetty positions
                    //determine the src and targetgeo points .
                    // Work out the vertical positions in a vertical layout
                    // in the edge buffer channels above and below this rank
                    let topChannelY = (this.rankTopY[parseInt(currentRank.toString(), 10)]
                        + this.rankBottomY[currentRank + 1]) / 2.0;
                    let bottomChannelY = (this.rankTopY[currentRank - 1]
                        + this.rankBottomY[parseInt(currentRank.toString(), 10)]) / 2.0;
                    if (reversed) {
                        const tmp = topChannelY;
                        topChannelY = bottomChannelY;
                        bottomChannelY = tmp;
                    }
                    if (layout.orientation === 'TopToBottom' || layout.orientation === 'BottomToTop') {
                        newPoints.push(this.getPointvalue(positionX, topChannelY + layout.marginY));
                        newPoints.push(this.getPointvalue(positionX, bottomChannelY + layout.marginY));
                    }
                    else {
                        newPoints.push(this.getPointvalue(topChannelY, positionX + layout.marginY));
                        newPoints.push(this.getPointvalue(bottomChannelY, positionX + layout.marginY));
                    }
                    this.limitX = Math.max(this.limitX, positionX);
                    currentRank += loopDelta;
                }
                // Second jetty of edge
                if (jettys != null) {
                    const arrayOffset = reversed ? 2 : 0;
                    const rankY = reversed ?
                        (layoutReversed ? this.rankTopY[parseInt(maxRank.toString(), 10)]
                            : this.rankBottomY[parseInt(maxRank.toString(), 10)])
                        : (layoutReversed ? this.rankBottomY[parseInt(minRank.toString(), 10)]
                            : this.rankTopY[parseInt(minRank.toString(), 10)]);
                    let jetty = jettys[parallelEdgeCount * 4 + 3 - arrayOffset];
                    if (reversed !== layoutReversed) {
                        jetty = -jetty;
                    }
                    const y = rankY - jetty;
                    const x = jettys[parallelEdgeCount * 4 + 2 - arrayOffset];
                    if (layout.orientation === 'TopToBottom' || layout.orientation === 'BottomToTop') {
                        newPoints.push(this.getPointvalue(x, y + layout.marginY));
                    }
                    else {
                        newPoints.push(this.getPointvalue(y, x + layout.marginY));
                    }
                }
                this.setEdgePoints(realEdge, newPoints, model);
                // Increase offset so next edge is drawn next to
                // this one
                if (offsetX === 0.0) {
                    offsetX = this.parallelEdgeSpacing;
                }
                else if (offsetX > 0) {
                    offsetX = -offsetX;
                }
                else {
                    offsetX = -offsetX + this.parallelEdgeSpacing;
                }
                parallelEdgeCount++;
            }
            cell.temp[0] = 101207;
        }
    }
    /* tslint:enable */
    // eslint-disable-next-line
    getPointvalue(x, y) {
        return { 'x': Number(x) || 0, 'y': Number(y) || 0 };
    }
    updateEdgeSetXYValue(model) {
        if (model.layout.enableLayoutRouting) {
            let isHorizontal = false;
            if (model.layout.orientation === 'LeftToRight' || model.layout.orientation === 'RightToLeft') {
                isHorizontal = true;
            }
            for (let i = 0; i < model.ranks.length; i++) {
                const rank = model.ranks[parseInt(i.toString(), 10)];
                for (let k = 0; k < rank.length; k++) {
                    const cell = rank[parseInt(k.toString(), 10)];
                    if ((cell).edges && (cell).edges.length > 0) {
                        const spacing = model.layout.horizontalSpacing > 0 ? (model.layout.horizontalSpacing / 2) : 15;
                        let check = true;
                        if (!(cell.minRank === i - 1 || cell.maxRank === i - 1)) {
                            check = false;
                        }
                        if (check) {
                            this.setXY(cell, i, undefined, isHorizontal ? true : false, model.ranks, spacing);
                        }
                    }
                }
            }
        }
    }
    getPreviousLayerConnectedCells(layer, cell) {
        if (cell.previousLayerConnectedCells == null) {
            cell.previousLayerConnectedCells = [];
            cell.previousLayerConnectedCells[0] = [];
            for (let i = 0; i < cell.connectsAsSource.length; i++) {
                const edge = cell.connectsAsSource[parseInt(i.toString(), 10)];
                if (edge.minRank === -1 || edge.minRank === layer - 1) {
                    // No dummy nodes in edge, add node of other side of edge
                    cell.previousLayerConnectedCells[0].push(edge.target);
                }
                else {
                    // Edge spans at least two layers, add edge
                    cell.previousLayerConnectedCells[0].push(edge);
                }
            }
        }
        return cell.previousLayerConnectedCells[0];
    }
    compare(a, b) {
        if (a != null && b != null) {
            if (b.weightedValue > a.weightedValue) {
                return -1;
            }
            else if (b.weightedValue < a.weightedValue) {
                return 1;
            }
        }
        return 0;
    }
    /* tslint:disable */
    // eslint-disable-next-line
    localEdgeProcessing(model, vertices) {
        // Iterate through each vertex, look at the edges connected in
        // both directions.
        for (let rankIndex = 0; rankIndex < model.ranks.length; rankIndex++) {
            const rank = model.ranks[parseInt(rankIndex.toString(), 10)];
            for (let cellIndex = 0; cellIndex < rank.length; cellIndex++) {
                const cell = rank[parseInt(cellIndex.toString(), 10)];
                if (this.crossReduction.isVertex(cell)) {
                    let currentCells = this.getPreviousLayerConnectedCells(rankIndex, cell);
                    let currentRank = rankIndex - 1;
                    // Two loops, last connected cells, and next
                    for (let k = 0; k < 2; k++) {
                        if (currentRank > -1
                            && currentRank < model.ranks.length
                            && currentCells != null
                            && currentCells.length > 0) {
                            const sortedCells = [];
                            for (let j = 0; j < currentCells.length; j++) {
                                const sorter = this.weightedCellSorter(currentCells[parseInt(j.toString(), 10)], this.getX(currentRank, currentCells[parseInt(j.toString(), 10)]));
                                sortedCells.push(sorter);
                            }
                            sortedCells.sort(this.compare);
                            cell.width = vertices[cell.id].cell.geometry.width;
                            cell.height = vertices[cell.id].cell.geometry.height;
                            let leftLimit;
                            if (model.layout.orientation === 'TopToBottom' || model.layout.orientation === 'BottomToTop') {
                                cell.x[0] = vertices[cell.id].cell.geometry.x + vertices[cell.id].cell.geometry.width / 2;
                                leftLimit = cell.x[0] - cell.width / 2 + vertices[cell.id].cell.geometry.height / 2;
                            }
                            else {
                                cell.x[0] = vertices[cell.id].cell.geometry.y;
                                leftLimit = cell.x[0];
                            }
                            let rightLimit = leftLimit + cell.width;
                            // Connected edge count starts at 1 to allow for buffer
                            // with edge of vertex
                            let connectedEdgeCount = 0;
                            let connectedEdgeGroupCount = 0;
                            const connectedEdges = [];
                            // Calculate width requirements for all connected edges
                            for (let j = 0; j < sortedCells.length; j++) {
                                const innerCell = sortedCells[parseInt(j.toString(), 10)].cell;
                                let connections;
                                if (this.crossReduction.isVertex(innerCell)) {
                                    // Get the connecting edge
                                    if (k === 0) {
                                        connections = cell.connectsAsSource;
                                    }
                                    else {
                                        connections = cell.connectsAsTarget;
                                    }
                                    for (let connIndex = 0; connIndex < connections.length; connIndex++) {
                                        if (connections[parseInt(connIndex.toString(), 10)].source === innerCell
                                            || connections[parseInt(connIndex.toString(), 10)].target === innerCell) {
                                            connectedEdgeCount += connections[parseInt(connIndex.toString(), 10)].edges
                                                .length;
                                            connectedEdgeGroupCount++;
                                            connectedEdges.push(connections[parseInt(connIndex.toString(), 10)]);
                                        }
                                    }
                                }
                                else {
                                    connectedEdgeCount += innerCell.edges.length;
                                    // eslint-disable-next-line
                                    connectedEdgeGroupCount++;
                                    connectedEdges.push(innerCell);
                                }
                            }
                            const requiredWidth = (connectedEdgeCount + 1)
                                * this.previousEdgeDistance;
                            // Add a buffer on the edges of the vertex if the edge count allows
                            if (cell.width > requiredWidth
                                + (2 * this.previousEdgeDistance)) {
                                leftLimit += this.previousEdgeDistance;
                                rightLimit -= this.previousEdgeDistance;
                            }
                            const availableWidth = rightLimit - leftLimit;
                            const edgeSpacing = availableWidth / connectedEdgeCount;
                            let currentX = leftLimit + edgeSpacing / 2.0;
                            let currentYOffset = this.minEdgeJetty - this.previousEdgeOffset;
                            let maxYOffset = 0;
                            for (let j = 0; j < connectedEdges.length; j++) {
                                const numActualEdges = connectedEdges[parseInt(j.toString(), 10)].edges
                                    .length;
                                if (this.jettyPositions === undefined) {
                                    this.jettyPositions = {};
                                }
                                let pos = this.jettyPositions[connectedEdges[parseInt(j.toString(), 10)].ids[0]];
                                if (pos == null) {
                                    pos = [];
                                    this.jettyPositions[connectedEdges[parseInt(j.toString(), 10)].ids[0]] = pos;
                                }
                                if (j < connectedEdgeCount / 2) {
                                    currentYOffset += this.previousEdgeOffset;
                                }
                                else if (j > connectedEdgeCount / 2) {
                                    currentYOffset -= this.previousEdgeOffset;
                                }
                                // Ignore the case if equals, this means the second of 2
                                // jettys with the same y (even number of edges)
                                for (let m = 0; m < numActualEdges; m++) {
                                    pos[m * 4 + k * 2] = currentX;
                                    currentX += edgeSpacing;
                                    pos[m * 4 + k * 2 + 1] = currentYOffset;
                                }
                                maxYOffset = Math.max(maxYOffset, currentYOffset);
                            }
                        }
                        currentCells = this.getNextLayerConnectedCells(rankIndex, cell);
                        currentRank = rankIndex + 1;
                    }
                }
            }
        }
    }
    /* tslint:enable */
    updateMultiOutEdgesPoints(clnode) {
        for (let i = 0; i < clnode.outEdges.length / 2; i++) {
            const connector1 = this.nameTable[clnode.outEdges[parseInt(i.toString(), 10)]];
            const connector2 = this.nameTable[clnode.outEdges[clnode.outEdges.length - (i + 1)]];
            const geometry = 'geometry';
            //900930: To exclude self-loop in layouts
            if (connector1.sourceID !== connector2.targetID && connector1.targetID !== connector2.sourceID) {
                connector2[`${geometry}`].points[0].y = connector1[`${geometry}`].points[0].y;
            }
        }
    }
    getNextLayerConnectedCells(layer, cell) {
        if (cell.nextLayerConnectedCells == null) {
            cell.nextLayerConnectedCells = [];
            cell.nextLayerConnectedCells[0] = [];
            for (let i = 0; i < cell.connectsAsTarget.length; i++) {
                const edge = cell.connectsAsTarget[parseInt(i.toString(), 10)];
                if (edge.maxRank === -1 || edge.maxRank === layer + 1) {
                    // Either edge is not in any rank or
                    // no dummy nodes in edge, add node of other side of edge
                    cell.nextLayerConnectedCells[0].push(edge.source);
                }
                else {
                    // Edge spans at least two layers, add edge
                    cell.nextLayerConnectedCells[0].push(edge);
                }
            }
        }
        return cell.nextLayerConnectedCells[0];
    }
    getX(layer, cell) {
        if (this.crossReduction.isVertex(cell)) {
            return cell.x[0];
        }
        else if (!this.crossReduction.isVertex(cell)) {
            return cell.x[layer - cell.minRank - 1] || cell.temp[layer - cell.minRank - 1];
        }
        return 0.0;
    }
    getGeometry(edge) {
        const geometry = 'geometry';
        return edge[`${geometry}`];
    }
    setEdgePoints(edge, points, model) {
        if (edge != null) {
            const geometryValue = 'geometry';
            const geometry = this.getGeometry(edge);
            if (points != null) {
                for (let i = 0; i < points.length; i++) {
                    // eslint-disable-next-line
                    points[i].x = points[i].x;
                    // eslint-disable-next-line
                    points[i].y = points[i].y;
                }
            }
            geometry.points = points;
            edge[`${geometryValue}`] = geometry;
        }
    }
    assignRankOffset(model) {
        if (model) {
            for (let i = 0; i < model.ranks.length; i++) {
                this.rankCoordinatesAssigment(i, model);
            }
        }
    }
    rankCoordinatesAssigment(rankValue, model) {
        const rank = model.ranks[parseInt(rankValue.toString(), 10)];
        const spacing = model.layout.horizontalSpacing;
        let localOffset;
        for (let i = 0; i < rank.length; i++) {
            if (this[rankValue + '_' + 'RankOffset'] === undefined) {
                this[rankValue + '_' + 'RankOffset'] = 0;
            }
            localOffset = rank[parseInt(i.toString(), 10)].x[0];
            if (this[rankValue + '_' + 'RankOffset'] < localOffset) {
                this[rankValue + '_' + 'RankOffset'] = localOffset + rank[parseInt(i.toString(), 10)].width / 2 + spacing;
            }
        }
    }
    getType(type) {
        if (type === 'internalVertex') {
            return 'internalVertex';
        }
        else {
            return 'internalEdge';
        }
    }
    updateRankValuess(model) {
        this.rankTopY = [];
        this.rankBottomY = [];
        for (let i = 0; i < model.ranks.length; i++) {
            this.rankTopY[parseInt(i.toString(), 10)] = Number.MAX_VALUE;
            this.rankBottomY[parseInt(i.toString(), 10)] = -Number.MAX_VALUE;
        }
    }
    setVertexLocationValue(cell, orientation, modelBounds) {
        const cellGeomtry = cell.cell.geometry;
        let positionX;
        let positionY;
        if (orientation === 'TopToBottom' || orientation === 'BottomToTop') {
            positionX = cellGeomtry.x;
            positionY = cellGeomtry.y;
        }
        else {
            positionX = cellGeomtry.y;
            positionY = cellGeomtry.x;
        }
        if (orientation === 'RightToLeft') {
            // eslint-disable-next-line
            positionX = cellGeomtry.y;
            positionY = modelBounds.width - cellGeomtry.x - cellGeomtry.height;
            this.rankBottomY[cell.minRank] = Math.max(this.rankBottomY[cell.minRank], positionY);
            this.rankTopY[cell.minRank] = Math.min(this.rankTopY[cell.minRank], positionY + cellGeomtry.height);
        }
        else {
            this.rankTopY[cell.minRank] = Math.min(this.rankTopY[cell.minRank], positionY);
            this.rankBottomY[cell.minRank] = Math.max(this.rankBottomY[cell.minRank], positionY + cellGeomtry.height);
        }
    }
    calculateRectValue(dnode) {
        const rect = { x: 0, y: 0, right: 0, bottom: 0, height: 0, width: 0 };
        rect.x = dnode.offsetX - dnode.actualSize.width / 2;
        rect.right = dnode.offsetX + dnode.actualSize.width / 2;
        rect.y = dnode.offsetY - dnode.actualSize.height / 2;
        rect.bottom = dnode.offsetY + dnode.actualSize.height / 2;
        return rect;
    }
    isNodeOverLap(dnode, layoutProp) {
        let nodeRect = { x: 0, y: 0, right: 0, bottom: 0, height: 0, width: 0 };
        for (let i = 0; i < this.vertices.length; i++) {
            let rect = { x: 0, y: 0, width: 0, height: 0 };
            //let tempnode1: INode;
            const tempnode1 = this.nameTable[this.vertices[parseInt(i.toString(), 10)].value];
            if (dnode.id !== tempnode1.id && tempnode1.offsetX !== 0 && tempnode1.offsetY !== 0) {
                nodeRect = this.calculateRectValue(dnode);
                rect = this.calculateRectValue(tempnode1);
                if (this.isIntersect(rect, nodeRect, layoutProp)) {
                    if (layoutProp.orientation === 'TopToBottom' || layoutProp.orientation === 'BottomToTop') {
                        dnode.offsetX += layoutProp.horizontalSpacing;
                    }
                    else {
                        dnode.offsetY += layoutProp.verticalSpacing;
                    }
                    this.isNodeOverLap(dnode, layoutProp);
                }
            }
        }
    }
    isIntersect(rect, nodeRect, layoutProp) {
        if (!(Math.floor(rect.right + layoutProp.horizontalSpacing) <= Math.floor(nodeRect.x) ||
            Math.floor(rect.x - layoutProp.horizontalSpacing) >= Math.floor(nodeRect.right)
            || Math.floor(rect.y - layoutProp.verticalSpacing) >= Math.floor(nodeRect.bottom)
            || Math.floor(rect.bottom + layoutProp.verticalSpacing) <= Math.floor(nodeRect.y))) {
            return true;
        }
        else {
            return false;
        }
    }
    /* eslint-disable */
    updateMargin(layoutProp, layout, modelBounds, viewPort) {
        const viewPortBounds = { x: 0, y: 0, width: viewPort.x, height: viewPort.y };
        //let layoutBounds: Rect;
        let bounds = {
            x: modelBounds.x, y: modelBounds.y,
            right: modelBounds.x + modelBounds.width,
            bottom: modelBounds.y + modelBounds.height
        };
        const layoutBounds = layoutProp.bounds ? layoutProp.bounds : viewPortBounds;
        if (layout.orientation === 'TopToBottom' || layout.orientation === 'BottomToTop') {
            switch (layoutProp.horizontalAlignment) {
                case 'Auto':
                case 'Left':
                    layout.marginX = (layoutBounds.x - bounds.x) + layoutProp.margin.left;
                    break;
                case 'Right':
                    layout.marginX = layoutBounds.x + layoutBounds.width - layoutProp.margin.right - bounds.right;
                    break;
                case 'Center':
                    layout.marginX = layoutBounds.x + layoutBounds.width / 2 - (bounds.x + bounds.right) / 2;
                    break;
            }
            switch (layoutProp.verticalAlignment) {
                case 'Top':
                    //const top: number;
                    const top = layoutBounds.y + layoutProp.margin.top;
                    layout.marginY = layout.orientation === 'TopToBottom' ? top : -top;
                    break;
                case 'Bottom':
                    //const bottom: number;
                    const bottom = layoutBounds.y + layoutBounds.height - layoutProp.margin.bottom;
                    layout.marginY = layout.orientation === 'TopToBottom' ? bottom - bounds.bottom : -(bottom - bounds.bottom);
                    break;
                case 'Auto':
                case 'Center':
                    //const center: number;
                    const center = layoutBounds.y + layoutBounds.height / 2;
                    layout.marginY = layout.orientation === 'TopToBottom' ?
                        center - (bounds.y + bounds.bottom) / 2 : -center + (bounds.y + bounds.bottom) / 2;
                    break;
            }
        }
        else {
            switch (layoutProp.horizontalAlignment) {
                case 'Auto':
                case 'Left':
                    //let left: number;
                    const left = layoutBounds.x + layoutProp.margin.left;
                    layout.marginX = layout.orientation === 'LeftToRight' ? left : -left;
                    break;
                case 'Right':
                    let right;
                    right = layoutBounds.x + layoutBounds.width - layoutProp.margin.right;
                    layout.marginX = layout.orientation === 'LeftToRight' ? right - bounds.right : bounds.right - right;
                    break;
                case 'Center':
                    let center;
                    center = layoutBounds.width / 2 + layoutBounds.x;
                    layout.marginX = layout.orientation === 'LeftToRight' ?
                        center - (bounds.y + bounds.bottom) / 2 : -center + (bounds.x + bounds.right) / 2;
                    break;
            }
            switch (layoutProp.verticalAlignment) {
                case 'Top':
                    layout.marginY = layoutBounds.y + layoutProp.margin.top - bounds.y;
                    break;
                case 'Auto':
                case 'Center':
                    layout.marginY = layoutBounds.y + layoutBounds.height / 2 - (bounds.y + bounds.bottom) / 2;
                    break;
                case 'Bottom':
                    layout.marginY = layoutBounds.y + layoutBounds.height - layoutProp.margin.bottom - bounds.bottom;
                    break;
            }
        }
    }
    /* eslint-enable */
    //Handles positioning the nodes
    placementStage(model, marginX, marginY) {
        const placementStage = this.coordinateAssignment(marginX, marginY, parent, model);
        placementStage.model = model;
        placementStage.widestRankValue = null;
        this.placementStageExecute(placementStage);
        return {
            marginX: placementStage.marginX + model.layout.horizontalSpacing,
            marginY: placementStage.marginY + model.layout.verticalSpacing
        };
    }
    //Initializes the layout properties for positioning
    coordinateAssignment(marginX, marginY, parent, model) {
        const plalementChange = {};
        if (model.layout.orientation === 'TopToBottom' || model.layout.orientation === 'BottomToTop') {
            plalementChange.horizontalSpacing = model.layout.horizontalSpacing;
            plalementChange.verticalSpacing = model.layout.verticalSpacing;
        }
        else {
            plalementChange.horizontalSpacing = model.layout.verticalSpacing;
            plalementChange.verticalSpacing = model.layout.horizontalSpacing;
        }
        plalementChange.orientation = 'north';
        //Removed the conditions here. So check here in case of any issue
        plalementChange.marginX = plalementChange.marginX = marginX;
        plalementChange.marginY = plalementChange.marginY = marginY;
        return plalementChange;
    }
    //Calculate the largest size of the node either height or width depends upon the layoutorientation
    calculateWidestRank(plalementChange, graph, model) {
        let isHorizontal = false;
        if (plalementChange.model.layout.orientation === 'LeftToRight' || plalementChange.model.layout.orientation === 'RightToLeft') {
            isHorizontal = true;
        }
        let offset = -plalementChange.verticalSpacing;
        let lastRankMaxCellSize = 0.0;
        plalementChange.rankSizes = [];
        plalementChange.rankOffset = [];
        for (let rankValue = model.maxRank; rankValue >= 0; rankValue--) {
            let maxCellSize = 0.0;
            const rank = model.ranks[parseInt(rankValue.toString(), 10)];
            let localOffset = isHorizontal ? plalementChange.marginY : plalementChange.marginX;
            for (let i = 0; i < rank.length; i++) {
                const node = rank[parseInt(i.toString(), 10)];
                if (this.crossReduction.isVertex(node)) {
                    const vertex = node;
                    if (vertex.cell && (vertex.cell.inEdges || vertex.cell.outEdges)) {
                        const obj = this.nameTable[vertex.cell.name];
                        vertex.width = obj.actualSize.width;
                        vertex.height = obj.actualSize.height;
                        maxCellSize = Math.max(maxCellSize, (isHorizontal ? vertex.width : vertex.height));
                    }
                }
                else {
                    if (node) {
                        const edge = node;
                        let numEdges = 1;
                        if (edge.edges != null) {
                            numEdges = edge.edges.length;
                        }
                        node.width = (numEdges - 1) * 10;
                    }
                }
                if (isHorizontal) {
                    if (!node.height) {
                        node.height = 0;
                    }
                }
                // Set the initial x-value as being the best result so far
                localOffset += (isHorizontal ? node.height : node.width) / 2.0;
                this.setXY(node, rankValue, localOffset, isHorizontal ? true : false);
                this.setTempVariable(node, rankValue, localOffset);
                localOffset += ((isHorizontal ? node.height : node.width) / 2.0) + plalementChange.horizontalSpacing;
                if (localOffset > plalementChange.widestRankValue) {
                    plalementChange.widestRankValue = localOffset;
                    plalementChange.widestRank = rankValue;
                }
                plalementChange.rankSizes[parseInt(rankValue.toString(), 10)] = localOffset;
            }
            plalementChange.rankOffset[parseInt(rankValue.toString(), 10)] = offset;
            const distanceToNextRank = maxCellSize / 2.0 + lastRankMaxCellSize / 2.0 + plalementChange.verticalSpacing;
            lastRankMaxCellSize = maxCellSize;
            if (plalementChange.orientation === 'north' || plalementChange.orientation === 'west') {
                offset += distanceToNextRank;
            }
            else {
                offset -= distanceToNextRank;
            }
            for (let i = 0; i < rank.length; i++) {
                const cell = rank[parseInt(i.toString(), 10)];
                this.setXY(cell, rankValue, offset, isHorizontal ? false : true);
            }
        }
    }
    /**
     * Sets the temp position of the node on the layer \
     *
     * @returns {  void }  Sets the temp position of the node on the layer \
     * @param {IVertex} node - provide the nodes value.
     * @param {number} layer - provide the layer value.
     * @param {number} value - provide the value value.
     * @private
     */
    setTempVariable(node, layer, value) {
        if (this.crossReduction.isVertex(node)) {
            node.temp[0] = value;
        }
        else {
            node.temp[layer - node.minRank - 1] = value;
        }
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * setXY method \
     *
     * @returns { void }     setXY method .\
     * @param {IVertex} node - provide the source value.
     * @param {number} layer - provide the target value.
     * @param {number} value - provide the layoutOrientation value.
     * @param {boolean} isY - provide the layoutOrientation value.
     * @param {IVertex[][]} ranks - provide the layoutOrientation value.
     * @param {number} spacing - provide the layoutOrientation value.
     *
     * @private
     */
    setXY(node, layer, value, isY, ranks, spacing) {
        if (node && node.cell) {
            if (node.cell.inEdges.length > 0 || node.cell.outEdges.length > 0) {
                if (isY) {
                    node.y[0] = value;
                }
                else {
                    node.x[0] = value;
                }
            }
            else {
                if (isY) {
                    node.y[layer - node.minRank - 1] = value;
                }
                else {
                    node.x[layer - node.minRank - 1] = value;
                }
            }
        }
        else {
            this.setEdgeXY(ranks, node, spacing, layer);
        }
    }
    //Sets geometry position of the layout node on the layout model
    rankCoordinates(stage, rankValue, graph, model) {
        let isHorizontal = false;
        if (stage.model.layout.orientation === 'LeftToRight' || stage.model.layout.orientation === 'RightToLeft') {
            isHorizontal = true;
        }
        const rank = model.ranks[parseInt(rankValue.toString(), 10)];
        let maxOffset = 0.0;
        let localOffset = (isHorizontal ? stage.marginY : stage.marginX)
            + (stage.widestRankValue - stage.rankSizes[parseInt(rankValue.toString(), 10)]) / 2;
        for (let i = 0; i < rank.length; i++) {
            const node = rank[parseInt(i.toString(), 10)];
            if (this.crossReduction.isVertex(node)) {
                const obj = this.nameTable[node.cell.name];
                node.width = obj.actualSize.width;
                node.height = obj.actualSize.height;
                maxOffset = Math.max(maxOffset, node.height);
            }
            else {
                const edge = node;
                let numEdges = 1;
                if (edge.edges != null) {
                    numEdges = edge.edges.length;
                }
                if (isHorizontal) {
                    node.height = (numEdges - 1) * 10;
                }
                else {
                    node.width = (numEdges - 1) * 10;
                }
            }
            const size = (isHorizontal ? node.height : node.width) / 2.0;
            localOffset += size;
            this.setXY(node, rankValue, localOffset, isHorizontal ? true : false);
            this.setTempVariable(node, rankValue, localOffset);
            localOffset += (size + stage.horizontalSpacing);
        }
    }
    //sets the layout in an initial positioning.it will arange all the ranks as much as possible
    initialCoords(plalementChange, facade, model) {
        this.calculateWidestRank(plalementChange, facade, model);
        // Reverse sweep direction each time from widest rank
        for (let i = plalementChange.widestRank; i >= 0; i--) {
            if (i < model.maxRank) {
                this.rankCoordinates(plalementChange, i, facade, model);
            }
        }
        for (let i = plalementChange.widestRank + 1; i <= model.maxRank; i++) {
            if (i > 0) {
                this.rankCoordinates(plalementChange, i, facade, model);
            }
        }
    }
    /**
     *  Checks whether the given node is an ancestor \
     *
     * @returns {  boolean }  Checks whether the given node is an ancestor \
     * @param {IVertex} node - provide the nodes value.
     * @param {IVertex} otherNode - provide the layer value.
     * @private
     */
    isAncestor(node, otherNode) {
        // Firstly, the hash code of this node needs to be shorter than the other node
        if (otherNode != null && node.hashCode != null && otherNode.hashCode != null
            && node.hashCode.length < otherNode.hashCode.length) {
            if (node.hashCode === otherNode.hashCode) {
                return true;
            }
            if (node.hashCode == null || node.hashCode == null) {
                return false;
            }
            for (let i = 0; i < node.hashCode.length; i++) {
                if (node.hashCode[parseInt(i.toString(), 10)] !== otherNode.hashCode[parseInt(i.toString(), 10)]) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }
    //initializes the sorter object
    weightedCellSorter(cell, weightedValue) {
        const weightedCellSorter = {};
        weightedCellSorter.cell = cell ? cell : null;
        weightedCellSorter.weightedValue = weightedValue ? weightedValue : 0;
        weightedCellSorter.visited = false;
        weightedCellSorter.rankIndex = null;
        return weightedCellSorter;
    }
    //Performs one node positioning in both directions
    minNode(plalementChange, model) {
        const nodeList = [];
        const map = { map: {} };
        const rank = [];
        for (let i = 0; i <= model.maxRank; i++) {
            rank[parseInt(i.toString(), 10)] = model.ranks[parseInt(i.toString(), 10)];
            for (let j = 0; j < rank[parseInt(i.toString(), 10)].length; j++) {
                const node = rank[parseInt(i.toString(), 10)][parseInt(j.toString(), 10)];
                const nodeWrapper = this.weightedCellSorter(node, i);
                nodeWrapper.rankIndex = j;
                nodeWrapper.visited = true;
                nodeList.push(nodeWrapper);
                model.setDictionaryForSorter(map, node, nodeWrapper, true);
            }
        }
        const maxTries = nodeList.length * 10;
        let count = 0;
        const tolerance = 1;
        while (nodeList.length > 0 && count <= maxTries) {
            const cellWrapper = nodeList.shift();
            const cell = cellWrapper.cell;
            const rankValue = cellWrapper.weightedValue;
            const rankIndex = cellWrapper.rankIndex;
            const nextLayerConnectedCells = this.crossReduction.getConnectedCellsOnLayer(cell, rankValue);
            const previousLayerConnectedCells = this.crossReduction.getConnectedCellsOnLayer(cell, rankValue, true);
            const nextConnectedCount = nextLayerConnectedCells ? nextLayerConnectedCells.length : 0;
            const prevConnectedCount = previousLayerConnectedCells ? previousLayerConnectedCells.length : 0;
            const medianNextLevel = this.medianXValue(plalementChange, nextLayerConnectedCells, rankValue + 1);
            const medianPreviousLevel = this.medianXValue(plalementChange, previousLayerConnectedCells, rankValue - 1);
            const numConnectedNeighbours = nextConnectedCount + prevConnectedCount;
            const currentPosition = this.crossReduction.getTempVariable(cell, rankValue);
            let cellMedian = currentPosition;
            if (numConnectedNeighbours > 0) {
                cellMedian = (medianNextLevel * nextConnectedCount + medianPreviousLevel * prevConnectedCount) / numConnectedNeighbours;
            }
            if (nextConnectedCount === 1 && prevConnectedCount === 1) {
                cellMedian = (medianPreviousLevel * prevConnectedCount) / prevConnectedCount;
            }
            else if (nextConnectedCount === 1) {
                cellMedian = (medianNextLevel * nextConnectedCount) / nextConnectedCount;
            }
            let positionChanged = false;
            let tempValue = undefined;
            if (cellMedian < currentPosition - tolerance) {
                if (rankIndex === 0) {
                    tempValue = cellMedian;
                    positionChanged = true;
                }
                else {
                    const leftCell = rank[parseInt(rankValue.toString(), 10)][rankIndex - 1];
                    let leftLimit = this.crossReduction.getTempVariable(leftCell, rankValue);
                    leftLimit = leftLimit + leftCell.width / 2 + plalementChange.intraCellSpacing + cell.width / 2;
                    if (leftLimit < cellMedian) {
                        tempValue = cellMedian;
                        positionChanged = true;
                    }
                    else if (leftLimit < this.crossReduction.getTempVariable(cell, rankValue) - tolerance) {
                        tempValue = leftLimit;
                        positionChanged = true;
                    }
                }
            }
            else if (cellMedian > currentPosition + tolerance) {
                const rankSize = rank[parseInt(rankValue.toString(), 10)].length;
                if (rankIndex === rankSize - 1) {
                    tempValue = cellMedian;
                    positionChanged = true;
                }
                else {
                    const rightCell = rank[parseInt(rankValue.toString(), 10)][rankIndex + 1];
                    let rightLimit = this.crossReduction.getTempVariable(rightCell, rankValue);
                    rightLimit = rightLimit - rightCell.width / 2 - plalementChange.intraCellSpacing - cell.width / 2;
                    if (rightLimit > cellMedian) {
                        tempValue = cellMedian;
                        positionChanged = true;
                    }
                    else if (rightLimit > this.crossReduction.getTempVariable(cell, rankValue) + tolerance) {
                        tempValue = rightLimit;
                        positionChanged = true;
                    }
                }
            }
            if (positionChanged) {
                this.setTempVariable(cell, rankValue, tempValue);
                // Add connected nodes to map and list
                this.updateNodeList(nodeList, map, nextLayerConnectedCells, model);
                this.updateNodeList(nodeList, map, previousLayerConnectedCells, model);
            }
            if (this.crossReduction.isVertex(cellWrapper.cell)) {
                cellWrapper.visited = false;
            }
            count++;
        }
    }
    //Updates the ndoes collection
    updateNodeList(nodeList, map, collection, model) {
        for (let i = 0; i < collection.length; i++) {
            const connectedCell = collection[parseInt(i.toString(), 10)];
            const connectedCellWrapper = model.getDictionaryForSorter(map, connectedCell);
            if (connectedCellWrapper != null) {
                if (connectedCellWrapper.visited === false) {
                    connectedCellWrapper.visited = true;
                    nodeList.push(connectedCellWrapper);
                }
            }
        }
    }
    //Calculates the node position of the connected cell on the specified rank
    medianXValue(plalementChange, connectedCells, rankValue) {
        if (!connectedCells || connectedCells.length === 0) {
            return 0;
        }
        const medianValues = [];
        for (let i = 0; i < connectedCells.length; i++) {
            medianValues[parseInt(i.toString(), 10)]
                = this.crossReduction.getTempVariable(connectedCells[parseInt(i.toString(), 10)], rankValue);
        }
        medianValues.sort((a, b) => {
            return a - b;
        });
        if (connectedCells.length % 2 === 1) {
            return medianValues[Math.floor(connectedCells.length / 2)];
        }
        else {
            const medianPoint = connectedCells.length / 2;
            const leftMedian = medianValues[medianPoint - 1];
            const rightMedian = medianValues[parseInt(medianPoint.toString(), 10)];
            return ((leftMedian + rightMedian) / 2);
        }
    }
    //Updates the geometry of the vertices
    placementStageExecute(plalementChange) {
        let isHorizontal = false;
        if (plalementChange.model.layout.orientation === 'LeftToRight' || plalementChange.model.layout.orientation === 'RightToLeft') {
            isHorizontal = true;
        }
        plalementChange.jettyPositions = {};
        const model = plalementChange.model;
        // eslint-disable-next-line
        isHorizontal ? plalementChange.currentYDelta = 0.0 : plalementChange.currentXDelta = 0.0;
        this.initialCoords(plalementChange, { model: model }, model);
        this.minNode(plalementChange, model);
        let bestOffsetDelta = 100000000.0;
        if (!plalementChange.maxIterations) {
            plalementChange.maxIterations = 8;
        }
        for (let i = 0; i < plalementChange.maxIterations; i++) {
            // if the total offset is less for the current positioning,
            //there are less heavily angled edges and so the current positioning is used
            if ((isHorizontal ? plalementChange.currentYDelta : plalementChange.currentXDelta) < bestOffsetDelta) {
                for (let j = 0; j < model.ranks.length; j++) {
                    const rank = model.ranks[parseInt(j.toString(), 10)];
                    for (let k = 0; k < rank.length; k++) {
                        const cell = rank[parseInt(k.toString(), 10)];
                        this.setXY(cell, j, this.crossReduction.getTempVariable(cell, j), isHorizontal ? true : false);
                    }
                }
                bestOffsetDelta = isHorizontal ? plalementChange.currentYDelta : plalementChange.currentXDelta;
            }
            // eslint-disable-next-line
            isHorizontal ? plalementChange.currentYDelta = 0 : plalementChange.currentXDelta = 0;
        }
        this.setCellLocations(plalementChange, model);
    }
    //sets the cell position in the after the layout operation
    setCellLocations(plalementChange, model) {
        const vertices = this.getValues(model.vertexMapper);
        for (let i = 0; i < vertices.length; i++) {
            this.setVertexLocation(plalementChange, vertices[parseInt(i.toString(), 10)]);
        }
    }
    //used to specify the geometrical position of the layout model cell
    garphModelsetVertexLocation(plalementChange, cell, x, y) {
        //let model: MultiParentModel = plalementChange.model;
        const geometry = cell.geometry;
        let result = null;
        if (geometry != null) {
            result = { x: x, y: y, width: geometry.width, height: geometry.height };
            if (geometry.x !== x || geometry.y !== y) {
                cell.geometry = result;
            }
        }
        return result;
    }
    //set the position of the specified node
    setVertexLocation(plalementChange, cell) {
        let isHorizontal = false;
        if (plalementChange.model.layout.orientation === 'LeftToRight' || plalementChange.model.layout.orientation === 'RightToLeft') {
            isHorizontal = true;
        }
        const realCell = cell.cell;
        const positionX = cell.x[0] - cell.width / 2;
        const positionY = cell.y[0] - cell.height / 2;
        this.garphModelsetVertexLocation(plalementChange, realCell, positionX, positionY);
        if (isHorizontal) {
            if (!plalementChange.marginY) {
                plalementChange.marginY = 0;
            }
            plalementChange.marginY = Math.max(plalementChange.marginY, positionY + cell.height);
        }
        else {
            if (!plalementChange.marginX) {
                plalementChange.marginX = 0;
            }
            plalementChange.marginX = Math.max(plalementChange.marginX, positionX + cell.width);
        }
    }
    /**
     *  get the specific value from the key value pair \
     *
     * @returns {  {}[] }  get the specific value from the key value pair \
     * @param {VertexMapper} mapper - provide the mapper value.
     * @private
     */
    getValues(mapper) {
        const list = [];
        if (mapper.map) {
            for (const key of Object.keys(mapper.map)) {
                list.push(mapper.map[`${key}`]);
            }
        }
        return list;
    }
    /**
     *Checks and reduces the crosses in between line segments \
     *
     * @returns { void }    Checks and reduces the crosses in between line segments.\
     * @param {End} model - provide the model value.
     *
     * @private
     */
    crossingStage(model) {
        this.crossReduction.execute(model);
    }
    //Initializes the ranks of the vertices
    layeringStage(model) {
        this.initialRank(model);
        this.fixRanks(model);
    }
    //determine the initial rank for the each vertex on the relevent direction
    initialRank(model) {
        const startNodes = model.startNodes;
        const internalNodes = model.getDictionaryValues(model.vertexMapper);
        const startNodesCopy = startNodes.slice();
        while (startNodes.length > 0) {
            const internalNode = startNodes[0];
            const layerDeterminingEdges = internalNode.connectsAsTarget;
            const edgesToBeMarked = internalNode.connectsAsSource;
            let allEdgesScanned = true;
            let minimumLayer = 100000000;
            for (let i = 0; i < layerDeterminingEdges.length; i++) {
                const internalEdge = layerDeterminingEdges[parseInt(i.toString(), 10)];
                if (internalEdge.temp[0] === 5270620) {
                    // This edge has been scanned, get the layer of the node on the other end
                    const otherNode = internalEdge.source;
                    minimumLayer = Math.min(minimumLayer, otherNode.temp[0] - 1);
                }
                else {
                    allEdgesScanned = false;
                    break;
                }
            }
            // If all edge have been scanned, assign the layer, mark all edges in the other direction and remove from the nodes list
            if (allEdgesScanned) {
                internalNode.temp[0] = minimumLayer;
                if (!model.maxRank) {
                    model.maxRank = 100000000;
                }
                model.maxRank = Math.min(model.maxRank, minimumLayer);
                if (edgesToBeMarked != null) {
                    for (let i = 0; i < edgesToBeMarked.length; i++) {
                        const internalEdge = edgesToBeMarked[parseInt(i.toString(), 10)];
                        internalEdge.temp[0] = 5270620;
                        // Add node on other end of edge to LinkedList of nodes to be analysed
                        const otherNode = internalEdge.target;
                        // Only add node if it hasn't been assigned a layer
                        if (otherNode.temp[0] === -1) {
                            startNodes.push(otherNode);
                            // Mark this other node as neither being unassigned nor assigned
                            //so it isn't added to this list again, but it's layer isn't used in any calculation.
                            otherNode.temp[0] = -2;
                        }
                    }
                }
                startNodes.shift();
            }
            else {
                // Not all the edges have been scanned, get to the back of the class and put the dunces cap on
                const removedCell = startNodes.shift();
                startNodes.push(internalNode);
                if (removedCell === internalNode && startNodes.length === 1) {
                    // This is an error condition, we can't get out of this loop.
                    //It could happen for more than one node but that's a lot harder to detect. Log the error
                    break;
                }
            }
        }
        for (let i = 0; i < internalNodes.length; i++) {
            internalNodes[parseInt(i.toString(), 10)].temp[0] -= model.maxRank;
        }
        for (let i = 0; i < startNodesCopy.length; i++) {
            const internalNode = startNodesCopy[parseInt(i.toString(), 10)];
            let currentMaxLayer = 0;
            const layerDeterminingEdges = internalNode.connectsAsSource;
            for (let j = 0; j < layerDeterminingEdges.length; j++) {
                const internalEdge = layerDeterminingEdges[parseInt(j.toString(), 10)];
                const otherNode = internalEdge.target;
                internalNode.temp[0] = Math.max(currentMaxLayer, otherNode.temp[0] + 1);
                currentMaxLayer = internalNode.temp[0];
            }
        }
        model.maxRank = 100000000 - model.maxRank;
    }
    //used to set the optimum value of each vertex on the layout
    fixRanks(model) {
        model.fixRanks();
    }
    //used to determine any cyclic stage have been created on the layout model
    cycleStage(model) {
        const seenNodes = {};
        model.startNodes = [];
        const unseenNodesArray = model.getDictionaryValues(model.vertexMapper);
        const unseenNodes = [];
        for (let i = 0; i < unseenNodesArray.length; i++) {
            unseenNodesArray[parseInt(i.toString(), 10)].temp[0] = -1;
            unseenNodes[unseenNodesArray[parseInt(i.toString(), 10)].id] = unseenNodesArray[parseInt(i.toString(), 10)];
        }
        let rootsArray = null;
        if (model.roots != null) {
            const modelRoots = model.roots;
            rootsArray = [];
            for (let i = 0; i < modelRoots.length; i++) {
                rootsArray[parseInt(i.toString(), 10)] = model.getDictionary(model.vertexMapper, modelRoots[parseInt(i.toString(), 10)]);
                if (rootsArray[parseInt(i.toString(), 10)] != null) {
                    model.startNodes.push(rootsArray[parseInt(i.toString(), 10)]);
                }
            }
        }
        model.visit('removeParentConnection', rootsArray, true, null, { seenNodes: seenNodes, unseenNodes: unseenNodes });
        const seenNodesCopy = model.clone(seenNodes, null, true);
        model.visit('removeNodeConnection', unseenNodes, true, seenNodesCopy, { seenNodes: seenNodes, unseenNodes: unseenNodes });
    }
    /**
     * removes the edge from the given collection \
     *
     * @returns {  IEdge }    removes the edge from the given collection .\
     * @param {IEdge} obj - provide the angle value.
     * @param { IEdge[]} array - provide the angle value.
     * @private
     */
    remove(obj, array) {
        const index = array.indexOf(obj);
        if (index !== -1) {
            array.splice(index, 1);
        }
        return obj;
    }
    /**
     * Inverts the source and target of an edge \
     *
     * @returns {  void }    Inverts the source and target of an edge .\
     * @param {IEdge} connectingEdge - provide the angle value.
     * @param { number} layer - provide the angle value.
     * @private
     */
    invert(connectingEdge, layer) {
        const temp = connectingEdge.source;
        connectingEdge.source = connectingEdge.target;
        connectingEdge.target = temp;
        connectingEdge.isReversed = !connectingEdge.isReversed;
    }
    /**
     * used to get the edges between the given source and target  \
     *
     * @returns {  IConnector[] }    used to get the edges between the given source and target  .\
     * @param {Vertex} source - provide the angle value.
     * @param { Vertex} target - provide the angle value.
     * @param { boolean} directed - provide the angle value.
     * @private
     */
    getEdgesBetween(source, target, directed) {
        const edges = this.getEdges(source);
        const result = [];
        for (let i = 0; i < edges.length; i++) {
            const src = this.getVisibleTerminal(edges[parseInt(i.toString(), 10)], true);
            const trg = this.getVisibleTerminal(edges[parseInt(i.toString(), 10)], false);
            if ((src === source && trg === target) || (!directed && src === target && trg === source)) {
                result.push(edges[parseInt(i.toString(), 10)]);
            }
        }
        return result;
    }
}
/**
 * Handles position the objects in a hierarchical tree structure
 */
class MultiParentModel {
    constructor(layout, vertices, roots, dlayout) {
        this.multiObjectIdentityCounter = 0;
        //used to count the no of times the parent have been used
        this.dfsCount = 0;
        this.hierarchicalUtil = new HierarchicalLayoutUtil();
        this.roots = roots;
        this.vertexMapper = { map: {} };
        const internalVertices = [];
        this.layout = dlayout;
        this.maxRank = 100000000;
        this.edgeMapper = { map: {} };
        this.hierarchicalLayout = layout;
        this.createInternalCells(layout, vertices, internalVertices, dlayout);
        for (let i = 0; i < vertices.length; i++) {
            const edges = internalVertices[parseInt(i.toString(), 10)].connectsAsSource;
            for (let j = 0; j < edges.length; j++) {
                const internalEdge = edges[parseInt(j.toString(), 10)];
                const realEdges = internalEdge.edges;
                if (realEdges != null && realEdges.length > 0) {
                    const realEdge = realEdges[0];
                    let targetCell = layout.getVisibleTerminal(realEdge, false);
                    let internalTargetCell = this.getDictionary(this.vertexMapper, targetCell);
                    if (internalVertices[parseInt(i.toString(), 10)] === internalTargetCell) {
                        targetCell = layout.getVisibleTerminal(realEdge, true);
                        internalTargetCell = this.getDictionary(this.vertexMapper, targetCell);
                    }
                    if (internalTargetCell != null && internalVertices[parseInt(i.toString(), 10)] !== internalTargetCell) {
                        internalEdge.target = internalTargetCell;
                        if (internalTargetCell.connectsAsTarget.length === 0) {
                            internalTargetCell.connectsAsTarget = [];
                        }
                        if (internalTargetCell.connectsAsTarget.indexOf(internalEdge) < 0) {
                            internalTargetCell.connectsAsTarget.push(internalEdge);
                        }
                    }
                }
            }
            internalVertices[parseInt(i.toString(), 10)].temp[0] = 1;
        }
    }
    /* tslint:disable */
    resetEdge(edge) {
        const geometry = { x: 0, y: 0, width: 0, height: 0, relative: true };
        const geo = geometry;
        edge['geometry'] = geo;
        return edge;
    }
    // eslint-disable-next-line max-len
    createInternalCells(layout, vertices, internalVertices, dlayout) {
        for (let i = 0; i < vertices.length; i++) {
            internalVertices[parseInt(i.toString(), 10)] = {
                x: [], y: [], temp: [], cell: vertices[parseInt(i.toString(), 10)],
                id: vertices[parseInt(i.toString(), 10)].name, connectsAsTarget: [], connectsAsSource: [], type: 'internalVertex'
            };
            this.setDictionary(this.vertexMapper, vertices[parseInt(i.toString(), 10)], internalVertices[parseInt(i.toString(), 10)]);
            const conns = layout.getEdges(vertices[parseInt(i.toString(), 10)]);
            internalVertices[parseInt(i.toString(), 10)].connectsAsSource = [];
            for (let j = 0; j < conns.length; j++) {
                const cell = layout.getVisibleTerminal(conns[parseInt(j.toString(), 10)], false);
                if (cell !== vertices[parseInt(i.toString(), 10)]) {
                    const undirectedEdges = layout.getEdgesBetween(vertices[parseInt(i.toString(), 10)], cell, false);
                    const directedEdges = layout.getEdgesBetween(vertices[parseInt(i.toString(), 10)], cell, true);
                    if (undirectedEdges != null && undirectedEdges.length > 0 && directedEdges.length * 2 >= undirectedEdges.length) {
                        const internalEdge = { x: [], y: [], temp: [], edges: undirectedEdges, ids: [] };
                        if (dlayout.enableLayoutRouting) {
                            for (let k = 0; k < undirectedEdges.length; k++) {
                                const edge = undirectedEdges[parseInt(k.toString(), 10)];
                                this.setDictionary(this.edgeMapper, undefined, internalEdge, edge.id);
                                // Resets all point on the edge and disables the edge style
                                // without deleting it from the cell style
                                this.resetEdge(edge);
                            }
                        }
                        internalEdge.source = internalVertices[parseInt(i.toString(), 10)];
                        for (let m = 0; m < undirectedEdges.length; m++) {
                            internalEdge.ids.push(undirectedEdges[parseInt(m.toString(), 10)].id);
                        }
                        internalEdge.source = internalVertices[parseInt(i.toString(), 10)];
                        if (!internalVertices[parseInt(i.toString(), 10)].connectsAsSource) {
                            internalVertices[parseInt(i.toString(), 10)].connectsAsSource = [];
                        }
                        if (internalVertices[parseInt(i.toString(), 10)].connectsAsSource.indexOf(internalEdge) < 0) {
                            internalVertices[parseInt(i.toString(), 10)].connectsAsSource.push(internalEdge);
                        }
                    }
                }
            }
            internalVertices[parseInt(i.toString(), 10)].temp[0] = 0;
        }
    }
    /* tslint:enable */
    /**
     * used to set the optimum value of each vertex on the layout \
     *
     * @returns {  void }   used to set the optimum value of each vertex on the layout .\
     * @private
     */
    fixRanks() {
        const rankList = [];
        this.ranks = [];
        for (let i = 0; i < this.maxRank + 1; i++) {
            rankList[parseInt(i.toString(), 10)] = [];
            this.ranks[parseInt(i.toString(), 10)] = rankList[parseInt(i.toString(), 10)];
        }
        let rootsArray = null;
        if (this.roots != null) {
            const oldRootsArray = this.roots;
            rootsArray = [];
            for (let i = 0; i < oldRootsArray.length; i++) {
                const cell = oldRootsArray[parseInt(i.toString(), 10)];
                const internalNode = this.getDictionary(this.vertexMapper, cell);
                rootsArray[parseInt(i.toString(), 10)] = internalNode;
            }
        }
        this.visit('updateMinMaxRank', rootsArray, false, null, { seenNodes: null, unseenNodes: null, rankList: rankList });
    }
    //Updates the min/max rank of the layer
    updateMinMaxRank(layer, seen, data) {
        //let seenNodes: {} = data.seenNodes;
        //let unseenNodes: {} = data.unseenNodes;
        const parent = data.parent;
        const node = data.root;
        const edge = data.edge;
        const rankList = data.rankList;
        if (!node.maxRank && node.maxRank !== 0) {
            node.maxRank = -1;
        }
        if (!node.minRank && node.minRank !== 0) {
            node.minRank = -1;
        }
        if (seen === 0 && node.maxRank < 0 && node.minRank < 0) {
            rankList[node.temp[0]].push(node);
            node.maxRank = node.temp[0];
            node.minRank = node.temp[0];
            node.temp[0] = rankList[node.maxRank].length - 1;
        }
        if (parent != null && edge != null) {
            const parentToCellRankDifference = parent.maxRank - node.maxRank;
            if (parentToCellRankDifference > 1) {
                edge.maxRank = parent.maxRank;
                edge.minRank = node.maxRank;
                edge.temp = [];
                edge.x = [];
                edge.y = [];
                for (let i = edge.minRank + 1; i < edge.maxRank; i++) {
                    rankList[parseInt(i.toString(), 10)].push(edge);
                    this.hierarchicalUtil.setTempVariable(edge, i, rankList[parseInt(i.toString(), 10)].length - 1);
                }
            }
        }
    }
    //used to store the value of th given key on the object
    setDictionary(dic, key, value, edgeId) {
        if (!edgeId) {
            const id = key.name;
            const previous = dic.map[`${id}`];
            dic.map[`${id}`] = value;
            return previous;
        }
        else {
            const previous = dic.map[`${edgeId}`];
            dic.map[`${edgeId}`] = value;
            return previous;
        }
    }
    /**
     * used to store the value of th given key on the objectt \
     *
     * @returns {  IVertex }   used to store the value of th given key on the object .\
     * @param {VertexMapper} dic - provide the angle value.
     * @param {IVertex} key - provide the angle value.
     * @param {WeightedCellSorter} value - provide the angle value.
     * @param {boolean} flag - provide the angle value.
     * @private
     */
    setDictionaryForSorter(dic, key, value, flag) {
        const id = key.id;
        if (!id) {
            //id = this._getDictionaryForSorter(dic, key);
        }
        const previous = dic.map[`${id}`];
        dic.map[`${id}`] = value;
        return previous;
    }
    /**
     * used to get the value of the given key \
     *
     * @returns {  IVertex }  used to get the value of the given key .\
     * @param {VertexMapper} dic - provide the angle value.
     * @param {IVertex} key - provide the angle value.
     * @private
     */
    getDictionary(dic, key) {
        if (!this.multiObjectIdentityCounter && this.multiObjectIdentityCounter !== 0) {
            this.multiObjectIdentityCounter = 0;
        }
        const id = key.name;
        if (!id) {
            if (!key.layoutObjectId) { ///####
                key.layoutObjectId = 'graphHierarchyNode#' + this.multiObjectIdentityCounter++;
                return key.layoutObjectId;
            }
            else {
                return dic.map[key.layoutObjectId];
            }
        }
        return dic.map[`${id}`];
    }
    /**
     * used to get the value of the given key \
     *
     * @returns {  IVertex }  used to get the value of the given key .\
     * @param {VertexMapper} dic - provide the angle value.
     * @param {IVertex} key - provide the angle value.
     * @private
     */
    getDictionaryForSorter(dic, key) {
        if (!this.multiObjectIdentityCounter && this.multiObjectIdentityCounter !== 0) {
            this.multiObjectIdentityCounter = 0;
        }
        const id = key.id;
        if (!id) {
            if (!key.layoutObjectId) { ///####
                key.layoutObjectId = 'graphHierarchyNode#' + this.multiObjectIdentityCounter++;
                return key.layoutObjectId;
            }
            else {
                return dic.map[key.layoutObjectId];
            }
        }
        return dic.map[`${id}`];
    }
    /**
     * used to get all the values of the dictionary object \
     *
     * @returns {  IVertex[] }  used to get all the values of the dictionary object .\
     * @param {VertexMapper} dic - provide the angle value.
     * @private
     */
    getDictionaryValues(dic) {
        const result = [];
        for (const key of Object.keys(dic.map)) {
            result.push(dic.map[`${key}`]);
        }
        return result;
    }
    /**
     * used to visit all the entries on the given dictionary with given function \
     *
     * @returns { void }  used to visit all the entries on the given dictionary with given function .\
     * @param {string} visitor - provide the visitor value.
     * @param {IVertex[]} dfsRoots - provide the dfsRoots value.
     * @param {boolean} trackAncestors - provide the trackAncestors value.
     * @param {{}} seenNodes - provide the seenNodes value.
     * @param {TraverseData} data - provide the data value.
     * @private
     */
    visit(visitor, dfsRoots, trackAncestors, seenNodes, data) {
        //let seenNodes1: {} = data.seenNodes;
        //let unseenNodes1: {} = data.unseenNodes;
        //let rankList: IVertex[][] = data.rankList;
        // Run depth first search through on all roots
        if (dfsRoots != null) {
            for (let i = 0; i < dfsRoots.length; i++) {
                const internalNode = dfsRoots[parseInt(i.toString(), 10)];
                if (internalNode != null) {
                    if (seenNodes == null) {
                        seenNodes = new Object();
                    }
                    data.parent = null;
                    data.root = internalNode;
                    data.edge = null;
                    if (trackAncestors) {
                        // Set up hash code for root
                        internalNode.hashCode = [];
                        internalNode.hashCode[0] = this.dfsCount;
                        internalNode.hashCode[1] = i;
                        this.extendedDfs(visitor, seenNodes, i, 0, data);
                    }
                    else {
                        this.depthFirstSearch(visitor, seenNodes, 0, data);
                    }
                }
            }
            this.dfsCount++;
        }
    }
    //used to perform the depth fisrt search on the layout model
    depthFirstSearch(visitor, seen, layer, data) {
        //let seenNodes1: {} = data.seenNodes;
        //let unseenNodes1: {} = data.unseenNodes;
        //let rankList: IVertex[][] = data.rankList;
        //let parent: IVertex = data.parent;
        const root = data.root;
        //let edge: IEdge = data.edge;
        if (root != null) {
            const rootId = root.id;
            if (seen[`${rootId}`] == null) {
                seen[`${rootId}`] = root;
                this.updateConnectionRank(visitor, layer, 0, data);
                // Copy the connects as source list so that visitors can change the original for edge direction inversions
                const outgoingEdges = root.connectsAsSource.slice();
                for (let i = 0; i < outgoingEdges.length; i++) {
                    const internalEdge = outgoingEdges[parseInt(i.toString(), 10)];
                    const targetNode = internalEdge.target;
                    // Root check is O(|roots|)
                    data.parent = root;
                    data.root = targetNode;
                    data.edge = internalEdge;
                    this.depthFirstSearch(visitor, seen, layer + 1, data);
                }
            }
            else {
                this.updateConnectionRank(visitor, layer, 1, data);
            }
        }
    }
    //Updates the rank of the connection
    updateConnectionRank(visitor, layer, seen, traversedList) {
        const parent = traversedList.parent;
        const root = traversedList.root;
        const edge = traversedList.edge;
        if (visitor === 'removeParentConnection' || visitor === 'removeNodeConnection') {
            const remove = visitor === 'removeNodeConnection';
            this.removeConnectionEdge(parent, root, edge, layer, traversedList, remove);
        }
        if (visitor === 'updateMinMaxRank') {
            this.updateMinMaxRank(layer, seen, traversedList);
        }
    }
    //Removes the edge from the collection
    removeConnectionEdge(parent, node, edge, layer, data, remove) {
        const seenNodes = data.seenNodes;
        const unseenNodes = data.unseenNodes;
        //let rankList: IVertex[][] = data.rankList;
        if (this.hierarchicalUtil.isAncestor(node, parent)) {
            this.hierarchicalUtil.invert(edge, 0);
            this.hierarchicalUtil.remove(edge, parent.connectsAsSource);
            if (remove) {
                node.connectsAsSource.push(edge);
                parent.connectsAsTarget.push(edge);
                this.hierarchicalUtil.remove(edge, node.connectsAsTarget);
            }
            else {
                parent.connectsAsTarget.push(edge);
                this.hierarchicalUtil.remove(edge, node.connectsAsTarget);
                node.connectsAsSource.push(edge);
            }
        }
        seenNodes[node.id] = node;
        delete unseenNodes[node.id];
    }
    //the dfs extends the default version by keeping track of cells ancestors, but it should be only used when necessary
    extendedDfs(visitor, seen, cHash, layer, data) {
        //let seenNodes: {} = data.seenNodes;
        //let unseenNodes: {} = data.unseenNodes;
        //let rankList: IVertex[][] = data.rankList;
        const parent = data.parent;
        const root = data.root;
        const edge = data.edge;
        if (root != null) {
            if (parent != null) {
                if (root.hashCode == null ||
                    root.hashCode[0] !== parent.hashCode[0]) {
                    const hashCodeLength = parent.hashCode.length + 1;
                    root.hashCode = parent.hashCode.slice();
                    root.hashCode[hashCodeLength - 1] = cHash;
                }
            }
            const rootId = root.id;
            if (seen[`${rootId}`] == null) {
                seen[`${rootId}`] = root;
                this.updateConnectionRank(visitor, layer, 0, data);
                const outgoingEdges = root.connectsAsSource.slice();
                for (let i = 0; i < outgoingEdges.length; i++) {
                    const internalEdge = outgoingEdges[parseInt(i.toString(), 10)];
                    const targetNode = internalEdge.target;
                    data.parent = root;
                    data.root = targetNode;
                    data.edge = internalEdge;
                    this.extendedDfs(visitor, seen, i, layer + 1, data);
                }
            }
            else {
                this.updateConnectionRank(visitor, layer, 1, data);
            }
        }
    }
    /**
     * used to clone the specified object ignoring all fieldnames in the given array of transient fields \
     *
     * @returns { void }    used to clone the specified object ignoring all fieldnames in the given array of transient fields .\
     * @param {Object} obj - provide the source value.
     * @param {string[]} transients - provide the target value.
     * @param {boolean} shallow - provide the shallow value.
     *
     * @private
     */
    clone(obj, transients, shallow) {
        if (obj != null && typeof (obj.constructor) === 'function') {
            const clonedObj = obj.constructor();
            for (const i of Object.keys(obj)) {
                if (i !== 'layoutObjectId' && (transients == null || transients.indexOf(i) < 0)) {
                    if (!shallow /*&& typeof (obj[`${i}`]) === 'object'*/) {
                        //not used
                        //  _clone[i] = $.extend(true, {}, obj[i]);
                    }
                    else {
                        clonedObj[`${i}`] = obj[`${i}`];
                    }
                }
            }
            return clonedObj;
        }
        return null;
    }
}
/**
 * Defines how to reduce the crosses in between the line segments
 */
class CrossReduction {
    /**
     *  used to calculate the number of edges crossing the layout model \
     *
     * @returns { number }  used to calculate the number of edges crossing the layout model\
     * @param {MultiParentModel} model - provide the model value.
     *
     * @private
     */
    calculateCrossings(model) {
        const numRanks = model.ranks.length;
        let totalCrossings = 0;
        for (let i = 1; i < numRanks; i++) {
            totalCrossings += this.calculateRankCrossing(i, model);
        }
        return totalCrossings;
    }
    /**
     *  used to get the temp value specified for the node or connector. \
     *
     * @returns { boolean }  used to get the temp value specified for the node or connector.\
     * @param {IVertex} node - provide the node value.
     * @param {IVertex} layer - provide the layer value.
     *
     * @private
     */
    getTempVariable(node, layer) {
        if (node) {
            if (this.isVertex(node)) {
                return node.temp[0];
            }
            else {
                return node.temp[layer - node.minRank - 1];
            }
        }
        return 0;
    }
    //used to specify the number of conenctors crossing between the specified rank and its below rank
    calculateRankCrossing(i, model) {
        let totalCrossings = 0;
        const rank = model.ranks[parseInt(i.toString(), 10)];
        const previousRank = model.ranks[i - 1];
        const tmpIndices = [];
        // Iterate over the top rank and fill in the connection information
        for (let j = 0; j < rank.length; j++) {
            const node = rank[parseInt(j.toString(), 10)];
            const rankPosition = this.getTempVariable(node, i);
            const connectedCells = this.getConnectedCellsOnLayer(node, i, true);
            ///####
            const nodeIndices = [];
            for (let k = 0; k < connectedCells.length; k++) {
                const connectedNode = connectedCells[parseInt(k.toString(), 10)];
                const otherCellRankPosition = this.getTempVariable(connectedNode, i - 1);
                nodeIndices.push(otherCellRankPosition);
            }
            nodeIndices.sort((x, y) => { return x - y; });
            tmpIndices[parseInt(rankPosition.toString(), 10)] = nodeIndices;
        }
        let indices = [];
        for (let j = 0; j < tmpIndices.length; j++) {
            indices = indices.concat(tmpIndices[parseInt(j.toString(), 10)]);
        }
        let firstIndex = 1;
        while (firstIndex < previousRank.length) {
            firstIndex <<= 1;
        }
        const treeSize = 2 * firstIndex - 1;
        firstIndex -= 1;
        const tree = [];
        for (let j = 0; j < treeSize; ++j) {
            tree[parseInt(j.toString(), 10)] = 0;
        }
        for (let j = 0; j < indices.length; j++) {
            const index = indices[parseInt(j.toString(), 10)];
            let treeIndex = index + firstIndex;
            ++tree[parseInt(treeIndex.toString(), 10)];
            while (treeIndex > 0) {
                if (treeIndex % 2) {
                    totalCrossings += tree[treeIndex + 1];
                }
                treeIndex = (treeIndex - 1) >> 1;
                ++tree[parseInt(treeIndex.toString(), 10)];
            }
        }
        return totalCrossings;
    }
    /**
     * Calculates and reduces the crosses between line segments
     *
     * @returns { void }Calculates and reduces the crosses between line segments.\
     * @param {MultiParentModel} model - provide the target value.
     * @private
     */
    execute(model) {
        // Stores initial ordering
        this.nestedBestRanks = [];
        for (let i = 0; i < model.ranks.length; i++) {
            this.nestedBestRanks[parseInt(i.toString(), 10)] = model.ranks[parseInt(i.toString(), 10)].slice();
        }
        let iterationsWithoutImprovement = 0;
        let currentBestCrossings = this.calculateCrossings(model);
        for (let i = 0; i < 24 && iterationsWithoutImprovement < 2; i++) {
            this.weightedMedian(i, model);
            const candidateCrossings = this.calculateCrossings(model);
            if (candidateCrossings < currentBestCrossings) {
                currentBestCrossings = candidateCrossings;
                iterationsWithoutImprovement = 0;
                for (let j = 0; j < this.nestedBestRanks.length; j++) {
                    const rank = model.ranks[parseInt(j.toString(), 10)];
                    for (let k = 0; k < rank.length; k++) {
                        const cell = rank[parseInt(k.toString(), 10)];
                        const obj = this.nestedBestRanks[parseInt(j.toString(), 10)][cell.temp[0]];
                        let check = true;
                        if (cell.edges && obj && !obj.edges) {
                            check = false;
                        }
                        if (obj && check) {
                            this.nestedBestRanks[parseInt(j.toString(), 10)][cell.temp[0]] = cell;
                        }
                    }
                }
            }
            else {
                // Increase count of iterations
                iterationsWithoutImprovement++;
                // Restore the best values to the cells
                for (let j = 0; j < this.nestedBestRanks.length; j++) {
                    const rank = model.ranks[parseInt(j.toString(), 10)];
                    for (let k = 0; k < rank.length; k++) {
                        const cell = rank[parseInt(k.toString(), 10)];
                        this.setTempVariable(cell, j, k);
                    }
                }
            }
            if (currentBestCrossings === 0) {
                break;
            }
        }
        // Store the best rankings but in the model
        const ranks = [];
        const rankList = [];
        for (let i = 0; i < model.maxRank + 1; i++) {
            rankList[parseInt(i.toString(), 10)] = [];
            ranks[parseInt(i.toString(), 10)] = rankList[parseInt(i.toString(), 10)];
        }
        for (let i = 0; i < this.nestedBestRanks.length; i++) {
            for (let j = 0; j < this.nestedBestRanks[parseInt(i.toString(), 10)].length; j++) {
                rankList[parseInt(i.toString(), 10)].push(this.nestedBestRanks[parseInt(i.toString(), 10)][parseInt(j.toString(), 10)]);
            }
        }
        model.ranks = ranks;
    }
    /**
     *  check whether the object is vertext or edge on the layout model. \
     *
     * @returns { boolean }  check whether the object is vertext or edge on the layout model..\
     * @param {IVertex} node - provide the iteration value.
     *
     * @private
     */
    isVertex(node) {
        if (node && node.cell && ((node.cell.inEdges && node.cell.inEdges.length) || (node.cell.outEdges && node.cell.outEdges.length))) {
            return true;
        }
        return false;
    }
    /**
     *  used to move up or move down the node position on the adjacent ranks \
     *
     * @returns { void }  used to move up or move down the node position on the adjacent ranks.\
     * @param {number} iteration - provide the iteration value.
     * @param {MultiParentModel} model - provide the model value.
     *
     * @private
     */
    weightedMedian(iteration, model) {
        // Reverse sweep direction each time through this method
        const downwardSweep = (iteration % 2 === 0);
        if (downwardSweep) {
            for (let j = model.maxRank - 1; j >= 0; j--) {
                this.medianRank(j, downwardSweep);
            }
        }
        else {
            for (let j = 1; j < model.maxRank; j++) {
                this.medianRank(j, downwardSweep);
            }
        }
    }
    /**
     * used to get the node next(up) connected to the specified node or connector \
     *
     * @returns { void } calculates the rank elements on the specified rank.\
     * @param {IVertex} cell - provide the cell value.
     * @param {number} layer - provide the layer value.
     * @param {boolean} isPrevious - provide the isPrevious value.
     *
     * @private
     */
    getConnectedCellsOnLayer(cell, layer, isPrevious = false) {
        let connectedlayer = 'nextLayerConnectedCells';
        let connectedAs = 'connectsAsTarget';
        if (isPrevious) {
            connectedlayer = 'previousLayerConnectedCells';
            connectedAs = 'connectsAsSource';
        }
        if (cell) {
            if (this.isVertex(cell)) {
                if (cell[`${connectedlayer}`] == null) {
                    cell[`${connectedlayer}`] = [];
                    cell[`${connectedlayer}`][0] = [];
                    for (let i = 0; i < cell[`${connectedAs}`].length; i++) {
                        const edge = cell[`${connectedAs}`][parseInt(i.toString(), 10)];
                        if (edge.maxRank === undefined) {
                            edge.maxRank = -1;
                        }
                        if (edge.maxRank === -1 || (isPrevious ? (edge.minRank === layer - 1) : (edge.maxRank === layer + 1))) {
                            // Either edge is not in any rank or no dummy nodes in edge, add node of other side of edge
                            cell[`${connectedlayer}`][0].push(isPrevious ? edge.target : edge.source);
                        }
                        else {
                            // Edge spans at least two layers, add edge
                            cell[`${connectedlayer}`][0].push(edge);
                        }
                    }
                }
                return cell[`${connectedlayer}`][0];
            }
            else {
                if (cell[`${connectedlayer}`] == null) {
                    cell[`${connectedlayer}`] = [];
                    for (let i = 0; i < cell.temp.length; i++) {
                        cell[`${connectedlayer}`][parseInt(i.toString(), 10)] = [];
                        if (i === (isPrevious ? 0 : (cell.temp.length - 1))) {
                            cell[`${connectedlayer}`][parseInt(i.toString(), 10)].push(isPrevious ? cell.target : cell.source);
                        }
                        else {
                            cell[`${connectedlayer}`][parseInt(i.toString(), 10)].push(cell);
                        }
                    }
                }
                return cell[`${connectedlayer}`][layer - cell.minRank - 1];
            }
        }
        return null;
    }
    /**
     * calculates the rank elements on the specified rank \
     *
     * @returns { void } calculates the rank elements on the specified rank.\
     * @param {IVertex[]} connectedCells - provide the cell value.
     * @param {number} rankValue - provide the layer value.
     *
     * @private
     */
    medianValue(connectedCells, rankValue) {
        const medianValues = [];
        let arrayCount = 0;
        for (let i = 0; i < connectedCells.length; i++) {
            const cell = connectedCells[parseInt(i.toString(), 10)];
            medianValues[arrayCount++] = this.getTempVariable(cell, rankValue);
        }
        // sorts numerical order sort
        medianValues.sort((a, b) => { return a - b; });
        if (arrayCount % 2 === 1) {
            // For odd numbers of adjacent vertices return the median
            return medianValues[Math.floor(arrayCount / 2)];
        }
        else if (arrayCount === 2) {
            return ((medianValues[0] + medianValues[1]) / 2.0);
        }
        else {
            const medianPoint = arrayCount / 2;
            const leftMedian = medianValues[medianPoint - 1] - medianValues[0];
            const rightMedian = medianValues[arrayCount - 1]
                - medianValues[parseInt(medianPoint.toString(), 10)];
            return (medianValues[medianPoint - 1] * rightMedian + medianValues[parseInt(medianPoint.toString(), 10)] * leftMedian)
                / (leftMedian + rightMedian);
        }
    }
    /**
     * get the temp value of the specified layer \
     *
     * @returns { void }     getDirection method .\
     * @param {IVertex} cell - provide the cell value.
     * @param {layer} layer - provide the layer value.
     * @param {LayoutOrientation} value - provide the value value.
     *
     * @private
     */
    setTempVariable(cell, layer, value) {
        if (cell) {
            cell.temp[0] = value;
        }
    }
    /**
     * used to minimize the node position on this rank and one of its adjacent ranks
     */
    medianRank(rankValue, downwardSweep) {
        const numCellsForRank = this.nestedBestRanks[parseInt(rankValue.toString(), 10)].length;
        const medianValues = [];
        const reservedPositions = [];
        for (let i = 0; i < numCellsForRank; i++) {
            const cell = this.nestedBestRanks[parseInt(rankValue.toString(), 10)][parseInt(i.toString(), 10)];
            const sorterEntry = { medianValue: 0 };
            sorterEntry.cell = cell;
            // Flip whether or not equal medians are flipped on up and down sweeps
            //TODO re-implement some kind of nudge medianValues[i].nudge = !downwardSweep;
            let nextLevelConnectedCells;
            if (downwardSweep) {
                nextLevelConnectedCells = this.getConnectedCellsOnLayer(cell, rankValue);
            }
            else {
                nextLevelConnectedCells = this.getConnectedCellsOnLayer(cell, rankValue, true);
            }
            let nextRankValue;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            downwardSweep ? nextRankValue = rankValue + 1 : nextRankValue = rankValue - 1;
            if (nextLevelConnectedCells != null && nextLevelConnectedCells.length !== 0) {
                sorterEntry.medianValue = this.medianValue(nextLevelConnectedCells, nextRankValue);
                medianValues.push(sorterEntry);
            }
            else {
                // Nodes with no adjacent vertices are flagged in the reserved array to
                //indicate they should be left in their current position.
                reservedPositions[this.getTempVariable(cell, rankValue)] = true;
            }
        }
        medianValues.sort(this.compare);
        // Set the new position of each node within the rank using its temp variable
        for (let i = 0; i < numCellsForRank; i++) {
            if (reservedPositions[parseInt(i.toString(), 10)] == null && medianValues.length > 0) {
                const cell = medianValues.shift().cell;
                this.setTempVariable(cell, rankValue, i);
            }
        }
    }
    //compares two values, it sends the values to the compare function,
    //and sorts the values according to the returned (negative, zero, positive) value
    compare(a, b) {
        if (a != null && b != null) {
            if (b.medianValue > a.medianValue) {
                return -1;
            }
            else if (b.medianValue < a.medianValue) {
                return 1;
            }
        }
        return 0;
    }
}
class MatrixModel {
    constructor() {
        this.edgeMapper = [];
    }
    /* tslint:disable */
    /** @private */
    /**
     * Arrange the elements
     *
     * @returns { void }  arrange the elements.\
     * @param {MatrixModelObject} matrixModel - provide the Matrix Model Object.
     * @param {Layout} layout - provide the layout value.
     *
     * @private
     */
    arrangeElements(matrixModel, layout) {
        const layoutSettings = matrixModel.model.layout;
        let isHorizontal;
        if (layout.orientation === 'LeftToRight' || layout.orientation === 'RightToLeft') {
            isHorizontal = true;
        }
        else {
            isHorizontal = false;
        }
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        //let spacingInverse: number = !isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        // Need to group element before
        this.groupLayoutCells(matrixModel);
        this.createMatrixCells(matrixModel);
        for (let j = 0; j < matrixModel.matrix.length; j++) {
            const matrixKey = matrixModel.matrix[parseInt(j.toString(), 10)].key;
            const matrixrow = matrixModel.matrix[parseInt(matrixKey.toString(), 10)].value;
            for (let i = 1; i < matrixrow.length; i++) {
                const cell = matrixrow[parseInt(i.toString(), 10)];
                const prevCell = matrixrow[i - 1];
                cell.offset += prevCell.offset + (prevCell.size / 2) + spacing + (cell.size / 2);
            }
        }
        // Sort roots based on their indices
        matrixModel.roots.sort(function (a, b) {
            if (a.cells[0] && b.cells[0]) {
                const indexA = matrixModel.model.roots.indexOf(a.cells[0].cell);
                const indexB = matrixModel.model.roots.indexOf(b.cells[0].cell);
                return indexA - indexB;
            }
            return 0;
        });
        for (let j = 0; j < matrixModel.roots.length; j++) {
            const root = matrixModel.roots[parseInt(j.toString(), 10)];
            this.arrangeMatrix(root, null, matrixModel);
        }
        for (let k = 0; k < matrixModel.matrix.length; k++) {
            const row = matrixModel.matrix[parseInt(k.toString(), 10)].value;
            for (let i = 0; i < row.length; i++) {
                const cell = row[parseInt(i.toString(), 10)];
                const parents = cell.visitedParents.slice();
                for (let i = 0; i < cell.visitedParents.length; i++) {
                    const cellIgnoredParent = cell.visitedParents[parseInt(i.toString(), 10)];
                    if (cellIgnoredParent && this.containsValue(cellIgnoredParent.ignoredChildren, cell)) {
                        parents.splice(parents.indexOf(cellIgnoredParent), 1);
                    }
                }
                if (parents.length > 1) {
                    let firstParent = parents[0];
                    let lastParent = parents[parents.length - 1];
                    const firstVertexParent = this.findParentVertexCellGroup(firstParent);
                    const lastVertexParent = this.findParentVertexCellGroup(lastParent);
                    if (firstParent !== firstVertexParent && firstVertexParent.offset < firstParent.offset) {
                        firstParent = firstVertexParent;
                    }
                    if (lastParent !== lastVertexParent && lastVertexParent.offset > lastParent.offset) {
                        lastParent = firstVertexParent;
                    }
                    const newoffset = (lastParent.offset + lastParent.size * 0.5 + firstParent.offset - firstParent.size * 0.5) / 2;
                    const availOffsetMin = cell.initialOffset;
                    const availOffsetMax = cell.offset;
                    if (!(availOffsetMax === availOffsetMin)) {
                        this.seenCells = [];
                        if (newoffset >= availOffsetMin && newoffset <= availOffsetMax) {
                            this.translateMatrixCells(newoffset - cell.offset, cell);
                        }
                        else if (newoffset < availOffsetMin) {
                            this.translateMatrixCells(availOffsetMin - cell.offset, cell);
                        }
                        this.seenCells = [];
                    }
                }
            }
        }
        this.setXYforMatrixCell(matrixModel);
    }
    /* tslint:enable */
    groupLayoutCells(matrixModel) {
        const ranks = matrixModel.model.ranks;
        for (let j = ranks.length - 1; j >= 0; j--) {
            const vertices = [];
            for (let v = 0; v < ranks[parseInt(j.toString(), 10)].length; v++) {
                const rank = ranks[parseInt(j.toString(), 10)][parseInt(v.toString(), 10)];
                const type = this.getType(rank.type);
                if (type === 'internalVertex') {
                    vertices.push(ranks[parseInt(j.toString(), 10)][parseInt(v.toString(), 10)]);
                }
            }
            const edges = [];
            for (let e = 0; e < ranks[parseInt(j.toString(), 10)].length; e++) {
                const rank = ranks[parseInt(j.toString(), 10)][parseInt(e.toString(), 10)];
                const type = this.getType(rank.type);
                if (type === 'internalEdge') {
                    edges.push(rank);
                }
            }
            while (vertices.length > 1) {
                const vertex1 = vertices[0];
                const parentset1 = this.selectIds(vertex1.connectsAsTarget, true);
                const childset1 = this.selectIds(vertex1.connectsAsSource, false);
                while (vertices.length > 1) {
                    const vertex2 = vertices[1];
                    const parentset2 = this.selectIds(vertex2.connectsAsTarget, true);
                    const childset2 = this.selectIds(vertex2.connectsAsSource, false);
                    const parentequals = this.compareLists(parentset1, parentset2);
                    const childequals = this.compareLists(childset1, childset2);
                    // 962088 - Connector overlapping with nodes in layout
                    const isParentsSameLevel = this.checkparentsLevel(parentset1, parentset2, matrixModel);
                    if (parentequals && childequals && isParentsSameLevel) {
                        this.updateMutualSharing(vertices[0], vertex2.id);
                        this.updateMutualSharing(vertices[1], vertex1.id);
                        vertices.splice(1, 1);
                        continue;
                    }
                    break;
                }
                vertices.splice(0, 1);
            }
            while (edges.length > 1) {
                const internalEdge = edges[0];
                const parentset = internalEdge.source;
                const childset = internalEdge.target;
                if (parentset.identicalSibiling != null) {
                    const groupedges = [];
                    for (let i = 0; i < edges.length; i++) {
                        const edge = edges[parseInt(i.toString(), 10)];
                        if (edge.target === childset) {
                            groupedges.push(edge);
                        }
                    }
                    for (let i = 0; i < groupedges.length; i++) {
                        const internalEdgese = groupedges[parseInt(i.toString(), 10)];
                        if (this.containsValue(parentset.identicalSibiling, internalEdgese.source.id)) {
                            internalEdgese.source.identicalSibiling = null;
                        }
                    }
                    internalEdge.source.identicalSibiling = null;
                }
                edges.splice(0, 1);
            }
        }
    }
    /* tslint:disable */
    createMatrixCells(matrixModel) {
        const layoutSettings = matrixModel.model.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight'
            || layoutSettings.orientation === 'RightToLeft';
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        const spacingInverse = !isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        const ranks = matrixModel.model.ranks;
        const matrixCellMapper = [];
        let rowoffset = -spacingInverse;
        for (let j = ranks.length - 1; j >= 0; j--) {
            let maxDimension = 0.0;
            const index = (ranks.length - 1) - j;
            const rank = ranks[parseInt(j.toString(), 10)].slice(); //.ToList();
            // Creating new row and adding it to matrix
            const matrixRow = [];
            matrixModel.matrix.push({ key: index, value: matrixRow });
            // Creating new row mapper
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tempMatrixRow = [];
            matrixCellMapper.push({ index: index, value: tempMatrixRow });
            while (rank.length > 0) //.Any())
             {
                const layoutCell = rank[0];
                // eslint-disable-next-line max-len
                const matrixCell = { level: index, parents: [], children: [], visitedParents: [], visitedChildren: [], ignoredChildren: [], cells: [], size: 0, offset: 0, initialOffset: 0 };
                matrixRow.push(matrixCell);
                const type = this.getType(layoutCell.type);
                if (type === 'internalVertex') {
                    matrixCell.cells.push(layoutCell);
                    if (layoutCell.identicalSibiling != null) {
                        for (let i = 0; i < rank.length; i++) {
                            const internalVertex = rank[parseInt(i.toString(), 10)];
                            const type = this.getType(internalVertex.type);
                            if (type === 'internalVertex' && this.containsValue(layoutCell.identicalSibiling, internalVertex.id)) {
                                matrixCell.cells.push(internalVertex);
                                if (matrixCell.cells.length > layoutCell.identicalSibiling.length) {
                                    break;
                                }
                            }
                        }
                    }
                    for (let i = 0; i < matrixCell.cells.length; i++) {
                        const internalVertex = matrixCell.cells[parseInt(i.toString(), 10)];
                        const type = this.getType(internalVertex.type);
                        if (type === 'internalVertex') {
                            const geometry = internalVertex.cell.geometry;
                            matrixCell.size += isHorizontal ? geometry.height : geometry.width;
                            maxDimension = Math.max(maxDimension, !isHorizontal ? geometry.height : geometry.width);
                            tempMatrixRow.push({ key: internalVertex.id, value: matrixCell });
                            if (internalVertex.connectsAsTarget.length > 0) {
                                for (let k = 0; k < internalVertex.connectsAsTarget.length; k++) {
                                    const internalEdgese = internalVertex.connectsAsTarget[parseInt(k.toString(), 10)];
                                    let key = null;
                                    if (this.containsValue(matrixCellMapper[index - 1].value, internalEdgese.ids)) {
                                        key = internalEdgese.ids;
                                    }
                                    else if (this.containsValue(matrixCellMapper[index - 1].value, internalEdgese.source.id)) {
                                        key = internalEdgese.source.id;
                                    }
                                    if (key != null) {
                                        const parentcellValue = matrixCellMapper[index - 1].value;
                                        let parentMartixCell;
                                        for (let v = 0; v < parentcellValue.length; v++) {
                                            if (parentcellValue[parseInt(v.toString(), 10)].key === key) {
                                                parentMartixCell = parentcellValue[parseInt(v.toString(), 10)].value;
                                                break;
                                            }
                                        }
                                        if (!this.containsValue(matrixCell.parents, parentMartixCell)) {
                                            matrixCell.parents.push(parentMartixCell);
                                        }
                                        if (!this.containsValue(parentMartixCell.children, matrixCell)) {
                                            parentMartixCell.children.push(matrixCell);
                                        }
                                    }
                                }
                            }
                            // 933466: Excessive Spacing Between Nodes in Complex Hierarchical Tree Layout
                            const vertex = matrixCell.cells[parseInt(i.toString(), 10)];
                            if (rank.indexOf(vertex) !== -1) {
                                rank.splice(rank.indexOf(vertex), 1);
                            }
                        }
                    }
                    matrixCell.size += (matrixCell.cells.length - 1) * spacing;
                }
                else if (type === 'internalEdge') {
                    matrixCell.cells.push(layoutCell);
                    for (let i = 0; i < matrixCell.cells.length; i++) {
                        const internalEdge = matrixCell.cells[parseInt(i.toString(), 10)];
                        const type1 = this.getType(internalEdge.type);
                        if (type1 === 'internalEdge' && internalEdge.edges != null) {
                            // need to spacing based on its source and target Node
                            const edgeSpacing = 5;
                            let cellSize = -edgeSpacing;
                            for (let k = 0; k < internalEdge.edges.length; k++) {
                                //const internalConnector = internalEdge.edges[k];
                                // need to summ up the line width
                                cellSize += 1 + edgeSpacing;
                            }
                            matrixCell.size += cellSize;
                        }
                        tempMatrixRow.push({ key: internalEdge.ids, value: matrixCell });
                        let key = null;
                        if (this.containsValue(matrixCellMapper[index - 1].value, internalEdge.ids)) {
                            key = internalEdge.ids;
                        }
                        else if (this.containsValue(matrixCellMapper[index - 1].value, internalEdge.source.id)) {
                            key = internalEdge.source.id;
                        }
                        if (key != null) {
                            const parentcell = matrixCellMapper[index - 1].value;
                            let parentMartixCell;
                            for (let v = 0; v < parentcell.length; v++) {
                                if (parentcell[parseInt(v.toString(), 10)].key === key) {
                                    parentMartixCell = parentcell[parseInt(v.toString(), 10)].value;
                                    break;
                                }
                            }
                            if (!this.containsValue(matrixCell.parents, parentMartixCell)) {
                                matrixCell.parents.push(parentMartixCell);
                            }
                            if (!this.containsValue(parentMartixCell.children, matrixCell)) {
                                parentMartixCell.children.push(matrixCell);
                            }
                        }
                        // 933466: Excessive Spacing Between Nodes in Complex Hierarchical Tree Layout
                        const vertex = matrixCell.cells[parseInt(i.toString(), 10)];
                        if (rank.indexOf(vertex) !== -1) {
                            rank.splice(rank.indexOf(vertex), 1);
                        }
                    }
                    matrixCell.size += (matrixCell.cells.length - 1) * spacing;
                }
                if (matrixCell.cells.length) {
                    const internalVertices = matrixCell.cells.filter((e) => e.type === 'internalVertex');
                    if (internalVertices.length > 0) {
                        for (const cell of internalVertices) {
                            const vertex = cell.cell;
                            if (matrixModel.model.roots.some((root) => root === vertex)) {
                                matrixModel.roots.push(matrixCell);
                                break;
                            }
                        }
                    }
                }
            }
            matrixModel.rowOffset.push(rowoffset + (maxDimension / 2) + spacingInverse);
            rowoffset += maxDimension + spacingInverse;
        }
    }
    arrangeMatrix(cell, parent, matrixModel) {
        const layoutSettings = matrixModel.model.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight'
            || layoutSettings.orientation === 'RightToLeft';
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        const matrix = matrixModel.matrix;
        const matrixRow = matrix[cell.level].value;
        const matrixIndex = matrixRow.indexOf(cell);
        if (cell.visitedParents.length > 0) {
            if (cell.visitedParents.length === 1) {
                cell.initialOffset = cell.offset;
            }
            if (!this.containsValue(cell.visitedParents, parent)) {
                const lastParent = cell.visitedParents[cell.visitedParents.length - 1];
                const parentMatrixRow = matrix[lastParent.level].value;
                const lastParentIndex = parentMatrixRow.indexOf(lastParent);
                const parentIndex = parentMatrixRow.indexOf(parent);
                const nextCell = matrixIndex + 1 < matrixRow.length ? matrixRow[matrixIndex + 1] : undefined;
                if (lastParent.ignoredChildren.indexOf(cell) !== -1 || parentIndex - lastParentIndex !== 1 ||
                    (nextCell && nextCell.visitedParents.length > 0)) {
                    cell.visitedParents.push(parent);
                    parent.ignoredChildren.push(cell);
                    return;
                }
            }
        }
        if (!(cell.children.length > 0)) {
            let validOffset = cell.offset;
            if (matrixIndex > 0) {
                const prevCell = matrixRow[matrixIndex - 1];
                validOffset = prevCell.offset + (prevCell.size / 2) + spacing + (cell.size / 2);
            }
            this.shiftMatrixCells(validOffset - cell.offset, cell, false, null, matrixModel);
        }
        else {
            for (let i = 0; i < cell.children.length; i++) {
                const matrixCellChild = cell.children[parseInt(i.toString(), 10)];
                if (!this.containsValue(cell.visitedChildren, matrixCellChild)) {
                    this.arrangeMatrix(matrixCellChild, cell, matrixModel);
                    cell.visitedChildren.push(matrixCellChild);
                }
            }
            if (cell.visitedChildren.length > 0) {
                const children = cell.visitedChildren.slice();
                for (let i = 0; i < cell.ignoredChildren.length; i++) {
                    const cellIgnoredChild = cell.ignoredChildren[parseInt(i.toString(), 10)];
                    if (this.containsValue(children, cellIgnoredChild)) {
                        children.splice(children.indexOf(cellIgnoredChild), 1);
                    }
                }
                if (children.length > 0) {
                    const firstChild = children[0];
                    const lastChild = children[children.length - 1];
                    const x1 = firstChild.offset - (firstChild.size / 2);
                    const x2 = lastChild.offset + (lastChild.size / 2);
                    const newoffset = (x1 + x2) / 2;
                    if (newoffset < cell.offset) {
                        this.shiftMatrixCells(cell.offset - newoffset, firstChild, true, cell, matrixModel);
                    }
                    else if (newoffset > cell.offset) {
                        this.shiftMatrixCells(newoffset - cell.offset, cell, false, null, matrixModel);
                    }
                }
            }
        }
        if (!this.containsValue(cell.visitedParents, parent)) {
            cell.visitedParents.push(parent);
        }
    }
    findParentVertexCellGroup(cell) {
        if (cell.cells[0]) {
            return cell;
        }
        if (cell.parents.length > 0) {
            return this.findParentVertexCellGroup(cell.parents[0]);
        }
        return cell;
    }
    translateMatrixCells(value, cell) {
        if (!(value === 0)) {
            if (this.seenCells.indexOf(cell) !== -1) {
                return;
            }
            this.seenCells.push(cell);
            cell.offset += value;
            if (cell.visitedChildren.length > 0) {
                for (let i = 0; i < cell.visitedChildren.length; i++) {
                    const cellVisitedChild = cell.visitedChildren[parseInt(i.toString(), 10)];
                    if (cell.ignoredChildren.indexOf(cellVisitedChild) !== -1) {
                        continue;
                    }
                    this.translateMatrixCells(value, cellVisitedChild);
                }
            }
        }
    }
    setXYforMatrixCell(matrixModel) {
        const layoutSettings = matrixModel.model.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight'
            || layoutSettings.orientation === 'RightToLeft';
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        for (let i = 0; i < matrixModel.matrix.length; i++) {
            const matrixrow1 = matrixModel.matrix[parseInt(i.toString(), 10)].value;
            for (let j = 0; j < matrixrow1.length; j++) {
                const matrixCell = matrixrow1[parseInt(j.toString(), 10)];
                let start = matrixCell.offset - (matrixCell.size / 2);
                for (let k = 0; k < matrixCell.cells.length; k++) {
                    const cell = matrixCell.cells[parseInt(k.toString(), 10)];
                    const type = this.getType(cell.type);
                    if (type === 'internalVertex') {
                        const internalVertex = cell;
                        const width = internalVertex.cell.geometry.width;
                        const height = internalVertex.cell.geometry.height;
                        if (isHorizontal) {
                            internalVertex.cell.geometry = new RectModel(matrixModel.rowOffset[matrixCell.level] - (width / 2), start, width, height);
                        }
                        else {
                            internalVertex.cell.geometry = new RectModel(start, matrixModel.rowOffset[matrixCell.level] - (height / 2), width, height);
                        }
                        start += (isHorizontal ? height : width) + spacing;
                    }
                    else if (type === 'internalEdge') {
                        const internalEdges = cell;
                        const parent = matrixCell.visitedParents[0];
                        let isContainSibilingVertex = false;
                        if (parent) {
                            for (let l = 0; l < parent.visitedChildren.length; l++) {
                                const children = parent.visitedChildren[parseInt(l.toString(), 10)];
                                const cells = [];
                                for (let m = 0; m < children.cells.length; m++) {
                                    const cell = children.cells[parseInt(m.toString(), 10)];
                                    const type = this.getType(cell.type);
                                    if (type === 'internalVertex') {
                                        cells.push(cell);
                                    }
                                }
                                if (cells.length > 0) {
                                    isContainSibilingVertex = true;
                                    break;
                                }
                            }
                        }
                        // Need to updated line width
                        const lineWidth = 1;
                        const edgeSpacing = 5;
                        for (let m = 0; m < internalEdges.edges.length; m++) {
                            const internalConnector = internalEdges.edges[parseInt(m.toString(), 10)];
                            let pt = this.getPointvalue(start + (lineWidth / 2.0), matrixModel.rowOffset[matrixCell.level]);
                            if (isHorizontal) {
                                pt = this.getPointvalue(matrixModel.rowOffset[matrixCell.level], start + (lineWidth / 2.0));
                            }
                            if (this.containsValue(this.getEdgeMapper(), internalConnector)) {
                                let key;
                                for (let l = 0; l < this.getEdgeMapper().length; l++) {
                                    if ((this.getEdgeMapper())[parseInt(l.toString(), 10)].key === internalConnector) {
                                        key = l;
                                        break;
                                    }
                                }
                                (this.getEdgeMapper())[parseInt(key.toString(), 10)].value.push(pt);
                            }
                            start += lineWidth + edgeSpacing;
                        }
                        start += spacing;
                    }
                }
            }
        }
    }
    getType(type) {
        if (type === 'internalVertex') {
            return 'internalVertex';
        }
        else {
            return 'internalEdge';
        }
    }
    selectIds(node, source) {
        const returnIds = [];
        for (let i = 0; i < node.length; i++) {
            const connector = node[parseInt(i.toString(), 10)];
            if (source) {
                {
                    returnIds.push(connector.source.id);
                }
            }
            else {
                returnIds.push(connector.target.id);
            }
        }
        return returnIds;
    }
    checkparentsLevel(list1, list2, matrixModel) {
        if (list1.length === 1 || list2.length === 1) {
            return true;
        }
        for (let i = 0; i < matrixModel.model.ranks.length; i++) {
            const rank = matrixModel.model.ranks[parseInt(i.toString(), 10)];
            if (rank.length < list1.length) {
                continue;
            }
            let objCount = 0;
            list1.forEach((obj1) => {
                if (rank.some((rankObj) => rankObj.id === obj1)) {
                    objCount++;
                }
            });
            if (objCount === list1.length) {
                return true;
            }
            else if (objCount > 0 && objCount !== list1.length) {
                return false;
            }
        }
        return false;
    }
    compareLists(list1, list2) {
        const newList1 = list1.slice();
        const newList2 = list2.slice();
        if (newList1.length === newList2.length) {
            if (newList1.length === 0) {
                return true;
            }
            else {
                let isSame = true;
                for (let i = 0; i < newList2.length; i++) {
                    const o = newList2[parseInt(i.toString(), 10)];
                    // EJ2-63944 - Nodes overlapping in Complex hierarchical tree layout in linear arrangement.
                    if (newList1.indexOf(o) === -1) {
                        isSame = false;
                        break;
                    }
                }
                return isSame;
            }
        }
        return false;
    }
    updateMutualSharing(cell, id) {
        if (cell.identicalSibiling != null) {
            cell.identicalSibiling.push(id);
        }
        else {
            cell.identicalSibiling = [];
            cell.identicalSibiling.push(id);
        }
    }
    containsValue(list, keyValue) {
        for (let i = 0; i < list.length; i++) {
            if (list[parseInt(i.toString(), 10)].key === keyValue
                || list[parseInt(i.toString(), 10)] === keyValue) {
                return true;
            }
        }
        return false;
    }
    shiftMatrixCells(value, startingCell, shiftChildren, parentCell, matrixModel) {
        if (!(value === 0)) {
            const matrix = matrixModel.matrix;
            const matrixRow = matrix[startingCell.level].value;
            const index = matrixRow.indexOf(startingCell);
            for (let i = index; i < matrixRow.length; i++) {
                matrixRow[parseInt(i.toString(), 10)].offset += value;
            }
            if (shiftChildren) {
                if (startingCell.visitedChildren.length > 0) {
                    let firstChild = undefined;
                    for (let i = 0; i < startingCell.visitedChildren.length; i++) {
                        const child = startingCell.visitedChildren[parseInt(i.toString(), 10)];
                        if (startingCell.ignoredChildren.indexOf(child) !== -1) {
                            continue;
                        }
                        firstChild = child;
                        break;
                    }
                    if (firstChild) {
                        this.shiftMatrixCells(value, firstChild, true, startingCell, matrixModel);
                    }
                }
                else {
                    let i = 1;
                    let nextSibilingwithChild = null;
                    while (index + i < matrixRow.length) {
                        const nextCell = matrixRow[index + i];
                        if (nextCell.visitedChildren.length > 0 && nextCell.visitedChildren.length > nextCell.ignoredChildren.length) {
                            nextSibilingwithChild = nextCell;
                            break;
                        }
                        i++;
                    }
                    if (nextSibilingwithChild != null) {
                        let firstChild = undefined;
                        for (let j = 0; j < nextSibilingwithChild.visitedChildren.length; j++) {
                            const child = nextSibilingwithChild.visitedChildren[parseInt(j.toString(), 10)];
                            if (nextSibilingwithChild.ignoredChildren.indexOf(child) !== -1) {
                                continue;
                            }
                            firstChild = child;
                            break;
                        }
                        if (firstChild) {
                            this.shiftMatrixCells(value, firstChild, true, nextSibilingwithChild, matrixModel);
                        }
                    }
                }
            }
        }
    }
    getPointvalue(x, y) {
        return { 'x': Number(x) || 0, 'y': Number(y) || 0 };
    }
    getEdgeMapper() {
        return this.edgeMapper;
    }
    setEdgeMapper(value) {
        this.edgeMapper.push(value);
    }
    // 962088 - Connector overlapping with nodes in layout
    findLevelDifference(connector) {
        let sourceLevel = 0;
        let targetLevel = 0;
        let difference = 0;
        if (this.diagram.layout.ranks) {
            const collection = this.diagram.layout.ranks;
            for (let i = 0; i < collection.length; i++) {
                for (let j = 0; j < collection[parseInt(i.toString(), 10)].length; j++) {
                    if (connector.sourceID === collection[parseInt(i.toString(), 10)][parseInt(j.toString(), 10)].id) {
                        sourceLevel = i;
                    }
                    if (connector.targetID === collection[parseInt(i.toString(), 10)][parseInt(j.toString(), 10)].id) {
                        targetLevel = i;
                    }
                }
            }
            difference = sourceLevel - targetLevel;
        }
        return difference < 0 ? -difference : difference;
    }
    updateLayout(viewPort, modelBounds, layoutProp, layout, nodeWithMultiEdges, nameTable, vertexTable) {
        let trnsX = ((viewPort.x - modelBounds.width) / 2) - modelBounds.x;
        let trnsY = ((viewPort.y - modelBounds.height) / 2) - modelBounds.y;
        trnsX = Math.round(trnsX);
        trnsY = Math.round(trnsY);
        const modifiedConnectors = [];
        const transModelBounds = new RectModel(modelBounds.x + trnsX, modelBounds.y + trnsY, modelBounds.width, modelBounds.height);
        const margin = layoutProp.margin;
        const isHorizontal = layout.orientation === 'RightToLeft' || layout.orientation === 'LeftToRight';
        const inversespacing = !isHorizontal ? layout.verticalSpacing : layout.horizontalSpacing;
        for (let i = 0; i < nodeWithMultiEdges.length; i++) {
            const node = nodeWithMultiEdges[parseInt(i.toString(), 10)];
            if (node.outEdges != null && node.outEdges.length > 0) {
                const count = node.outEdges.length;
                for (let j = 0; j < count; j++) {
                    const internalConnector = nameTable[node.outEdges[parseInt(j.toString(), 10)]];
                    internalConnector['pointCollection'] = [];
                    let segmentsize = inversespacing / 2.0;
                    let intermediatePoint = null;
                    // 962088 - Connector overlapping with nodes in layout
                    let isMultiLevel = false;
                    const levelDiff = this.findLevelDifference(internalConnector);
                    if (levelDiff > 1) {
                        isMultiLevel = true;
                    }
                    if (count > 1 || isMultiLevel) {
                        let key = -1;
                        const edgeMapper = this.getEdgeMapper();
                        for (let k = 0; k < edgeMapper.length; k++) {
                            if (edgeMapper[parseInt(k.toString(), 10)].key === internalConnector) {
                                key = k;
                                break;
                            }
                        }
                        //952617 - Diagram mixes straight and orthogonal segments.
                        if (key !== -1 && edgeMapper[parseInt(key.toString(), 10)].value.length > 0 && internalConnector.type === 'Orthogonal') {
                            const edgePoint = edgeMapper[parseInt(key.toString(), 10)].value[0];
                            const dxValue1 = edgePoint.x + layout.marginX;
                            const dyValue1 = edgePoint.y + layout.marginY;
                            let x1 = dxValue1;
                            let y1 = dyValue1;
                            if (layout.orientation === 'BottomToTop') {
                                y1 = modelBounds.height - dyValue1;
                            }
                            else if (layout.orientation === 'RightToLeft') {
                                x1 = modelBounds.width - dxValue1;
                            }
                            intermediatePoint = this.getPointvalue(x1, y1);
                            internalConnector.segments = [];
                            this.diagram.connectorPropertyChange(internalConnector, {}, {
                                type: 'Orthogonal',
                                segments: internalConnector.segments
                            });
                        }
                    }
                    let pts = [];
                    for (let i = 0; i < internalConnector.segments.length; i++) {
                        const pt = internalConnector.segments[parseInt(i.toString(), 10)].points;
                        // eslint-disable-next-line guard-for-in
                        for (const temp in pt) {
                            pts.push(pt[parseInt(temp.toString(), 10)]);
                        }
                    }
                    //951916 - connector overlaps on nodes of varying size.
                    let isVariableSizeNode = false;
                    if (pts.length === 4 && (count > 1 && !intermediatePoint || !intermediatePoint)) {
                        const distance = isHorizontal ? pts[3].x - pts[0].x : pts[3].y - pts[0].y;
                        if (distance > inversespacing) {
                            const sourceVertex = vertexTable[internalConnector.sourceID];
                            const spaceDiff = isHorizontal
                                ? sourceVertex.maxRowWidth - sourceVertex.geometry.width
                                : sourceVertex.maxRowHeight - sourceVertex.geometry.height;
                            if (spaceDiff > 0) {
                                segmentsize += spaceDiff / 2;
                                isVariableSizeNode = true;
                            }
                        }
                    }
                    // 962088 - Connector overlapping with nodes in layout
                    if (count > 1 || isVariableSizeNode || isMultiLevel) {
                        // eslint-disable-next-line max-len
                        pts = this.updateConnectorPoints(pts, segmentsize, intermediatePoint, transModelBounds, layout.orientation);
                        // 933466: Excessive Spacing Between Nodes in Complex Hierarchical Tree Layout
                        if ((intermediatePoint != null && this.diagram.layout.connectionPointOrigin !== 'DifferentPoint') || isVariableSizeNode) {
                            for (let p = 0; p < pts.length; p++) {
                                const pt = pts[parseInt(p.toString(), 10)];
                                internalConnector['pointCollection'].push(this.getPointvalue(pt.x, pt.y));
                            }
                        }
                        this.resetConnectorPoints(internalConnector);
                    }
                    modifiedConnectors.push(internalConnector);
                }
            }
            if (node.inEdges != null && node.inEdges.length > 1) {
                const count = node.inEdges.length;
                const edgeMapper = this.getEdgeMapper();
                for (let j = 0; j < count; j++) {
                    const internalConnector = nameTable[node.inEdges[parseInt(j.toString(), 10)]];
                    if (!this.containsValue(modifiedConnectors, internalConnector)) {
                        internalConnector['pointCollection'] = [];
                    }
                    if (count > 1) {
                        const segmentsize = inversespacing / 2.0;
                        let intermediatePoint = null;
                        let key = -1;
                        for (let k = 0; k < edgeMapper.length; k++) {
                            if (edgeMapper[parseInt(k.toString(), 10)].key === internalConnector) {
                                key = k;
                                break;
                            }
                        }
                        //952617 - Diagram mixes straight and orthogonal segments.
                        if (key !== -1 && edgeMapper[parseInt(key.toString(), 10)].value.length > 0
                            && !this.containsValue(modifiedConnectors, internalConnector) && internalConnector.type === 'Orthogonal') {
                            const edgePt = edgeMapper[parseInt(key.toString(), 10)].value[0];
                            const dx1 = edgePt.x + layout.marginX;
                            const dy1 = edgePt.y + layout.marginY;
                            // eslint-disable-next-line one-var
                            let x1 = dx1, y1 = dy1;
                            if (layout.orientation === 'BottomToTop') {
                                y1 = modelBounds.height - dy1;
                            }
                            else if (layout.orientation === 'RightToLeft') {
                                x1 = modelBounds.width - dx1;
                            }
                            intermediatePoint = this.getPointvalue(x1, y1);
                            internalConnector.segments = [];
                            this.diagram.connectorPropertyChange(internalConnector, {}, {
                                type: 'Orthogonal',
                                segments: internalConnector.segments
                            });
                        }
                        let pts = [];
                        for (let p = 0; p < internalConnector.segments.length; p++) {
                            const pt = internalConnector.segments[parseInt(p.toString(), 10)].points;
                            // eslint-disable-next-line guard-for-in
                            for (const temp in pt) {
                                pts.push(pt[parseInt(temp.toString(), 10)]);
                            }
                        }
                        pts.reverse();
                        // eslint-disable-next-line
                        pts = this.updateConnectorPoints(pts, segmentsize, intermediatePoint, transModelBounds, layoutProp.orientation);
                        pts.reverse();
                        internalConnector['pointCollection'] = [];
                        // 933466: Excessive Spacing Between Nodes in Complex Hierarchical Tree Layout
                        if (intermediatePoint != null && this.diagram.layout.connectionPointOrigin !== 'DifferentPoint') {
                            for (let p = 0; p < pts.length; p++) {
                                const pt = pts[parseInt(p.toString(), 10)];
                                internalConnector['pointCollection'].push(this.getPointvalue(pt.x, pt.y));
                            }
                        }
                        this.resetConnectorPoints(internalConnector);
                    }
                }
            }
        }
    }
    inflate(rect, x, y) {
        rect.x -= x;
        rect.y -= y;
        rect.width += 2 * x;
        rect.height += 2 * y;
        return rect;
    }
    updateConnectorPoints(connectorPoints, startSegmentSize, intermediatePoint, bounds, orientation) {
        // 933466: Excessive Spacing Between Nodes in Complex Hierarchical Tree Layout
        const layoutBounds = bounds;
        const isHorizontal = orientation === 'LeftToRight' || orientation === 'RightToLeft';
        const pts = connectorPoints;
        const startPoint = pts[0];
        const endPoint = pts[pts.length - 1];
        if (intermediatePoint != null) {
            const startNext = Point.transform(startPoint, Point.findAngle(startPoint, pts[1]), startSegmentSize);
            const endBefore = Point.transform(endPoint, Point.findAngle(endPoint, pts[pts.length - 2]), startSegmentSize);
            const intermediateStart = this.getPointvalue(intermediatePoint.x, startNext.y);
            const intermediateEnd = this.getPointvalue(intermediatePoint.x, endBefore.y);
            if (isHorizontal) {
                intermediateStart.x = startNext.x;
                intermediateStart.y = intermediatePoint.y;
                intermediateEnd.x = endBefore.x;
                intermediateEnd.y = intermediatePoint.y;
            }
            const length = Math.abs(Point.findLength(intermediateEnd, endBefore));
            if (length === 0) {
                return [startPoint, startNext, intermediateStart, endPoint];
            }
            return [startPoint, startNext, intermediateStart, intermediateEnd, endBefore, endPoint];
        }
        else if (pts.length === 4) {
            const startNext = Point.transform(startPoint, Point.findAngle(startPoint, pts[1]), startSegmentSize);
            const intermediateStart = this.getPointvalue(pts[2].x, startNext.y);
            if (isHorizontal) {
                intermediateStart.x = startNext.x;
                intermediateStart.y = pts[2].y;
            }
            return [startPoint, startNext, intermediateStart, endPoint];
        }
        return pts;
    }
    resetConnectorPoints(edge) {
        // 933466: Excessive Spacing Between Nodes in Complex Hierarchical Tree Layout
        if (edge['pointCollection'] && edge['pointCollection'].length > 0) {
            const connector = edge;
            connector.sourcePoint = edge['pointCollection'][0];
            connector.targetPoint = edge['pointCollection'][edge['pointCollection'].length - 1];
            const segments = [];
            for (let i = 0; i < edge['pointCollection'].length - 1; i++) {
                const point1 = edge['pointCollection'][parseInt(i.toString(), 10)];
                const point2 = edge['pointCollection'][i + 1];
                let length = findDistance(point1, point2);
                const direction = getConnectorDirection(point1, point2);
                if (i === edge['pointCollection'].length - 2) {
                    if ((this.diagram.layout.orientation === 'RightToLeft' && direction === 'Left')
                        || (this.diagram.layout.orientation === 'LeftToRight' && direction === 'Right')
                        || (this.diagram.layout.orientation === 'TopToBottom' && direction === 'Bottom')
                        || (this.diagram.layout.orientation === 'BottomToTop' && direction === 'Top')) {
                        length = length / 2;
                    }
                }
                /* tslint:enable */
                const tempSegment = new OrthogonalSegment(edge, 'segments', { type: 'Orthogonal' }, true);
                tempSegment.length = length;
                tempSegment.direction = direction;
                segments.push(tempSegment);
            }
            connector.segments = segments;
            connector.type = 'Orthogonal';
            this.diagram.connectorPropertyChange(connector, {}, {
                type: 'Orthogonal',
                segments: connector.segments
            });
        }
        else if (this.diagram.layout.connectionPointOrigin === 'DifferentPoint') {
            const obstacleCollection = 'obstaclePointCollection';
            if (edge.segments[0].points
                && edge.segments[0].points.length > 0 && edge[`${obstacleCollection}`]) {
                const connector = edge;
                connector.sourcePoint = edge[`${obstacleCollection}`][0];
                connector.targetPoint = edge[`${obstacleCollection}`][edge[`${obstacleCollection}`].length - 1];
                const segments = [];
                for (let i = 0; i < edge[`${obstacleCollection}`].length - 1; i++) {
                    const point1 = edge[`${obstacleCollection}`][parseInt(i.toString(), 10)];
                    const point2 = edge[`${obstacleCollection}`][i + 1];
                    let length = findDistance(point1, point2);
                    const direction = getConnectorDirection(point1, point2);
                    if (i === edge[`${obstacleCollection}`].length - 2) {
                        if ((this.diagram.layout.orientation === 'RightToLeft' && direction === 'Left')
                            || (this.diagram.layout.orientation === 'LeftToRight' && direction === 'Right')
                            || (this.diagram.layout.orientation === 'TopToBottom' && direction === 'Bottom')
                            || (this.diagram.layout.orientation === 'BottomToTop' && direction === 'Top')) {
                            length = length / 2;
                        }
                    }
                    /* tslint:enable */
                    const tempSegment = new OrthogonalSegment(edge, 'segments', { type: 'Orthogonal' }, true);
                    tempSegment.length = length;
                    tempSegment.direction = direction;
                    segments.push(tempSegment);
                }
                connector.segments = segments;
                connector.type = 'Orthogonal';
                this.diagram.connectorPropertyChange(connector, {}, {
                    type: 'Orthogonal',
                    segments: connector.segments
                });
            }
        }
    }
    nodePropertyChange(dnode) {
        // 933466: Excessive Spacing Between Nodes in Complex Hierarchical Tree Layout
        this.diagram.nodePropertyChange(dnode, {}, { offsetX: dnode.offsetX, offsetY: dnode.offsetY });
    }
}
